import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useDrag } from '@use-gesture/react';
import type {
  ContentAccessMetadata,
  MediaItem,
  MediaFitMode,
  Notice,
  Playlist,
  PlaylistItem,
  PlaylistTransitionType,
  ScheduleType,
  TimelineBlock,
  WebVideoMeta,
  WebVideoProvider,
} from '@cornerstone/shared';
import { isTemplateNotice } from '@cornerstone/shared';
import { isCatalogTemplate } from '../components/registeredNoticeTemplates';
import { TRANSITION_MIN_MS, TRANSITION_MAX_MS, TRANSITION_DEFAULT_MS } from '@cornerstone/shared';
import { PageContainer } from '../nav/PageContainer';
import { PageHeader } from '../nav/Breadcrumb';
import { useAdminRole } from '../nav/useAdminRole';
import { useCalibrationStore } from '../store/calibration';
import { useLibraryStore } from '../store/library';
import { api } from '../client/api';
import { playlistPreviewUrl } from '../client/previewUrl';
import { MEDIA_ACCEPT, isAllowedMediaFile } from '../client/mediaAccept';
import { Button, CtrlLabel, Field, Panel, Segmented } from '../components/ui';
import { useIsMobile } from '../utils/useIsMobile';
import { ToolbarMenu, ToolbarButton, MenuRow, MenuDivider, InfoTooltip, ModalShell, ConfirmModal } from '../components/menu';
import { ContentUsageNotice, useBulkContentUsage, useContentUsage } from '../components/ContentUsageNotice';
import { ContentOwnerGroupField, applyOwnerGroup, ownerGroupOf } from '../components/ContentOwnerGroupField';
import { addToPlaylistBlockedReason } from '../components/groupSections';
import { NoticeEditorModal } from '../components/NoticeEditorModal';
import { IdleContentSettings } from '../components/IdleContentSettings';
import { useBoOperator } from '../nav/useBoOperator';
import { WebUrlAddModal } from '../components/WebUrlAddModal';
import { useWebVideoUrlEnabled } from '../client/webVideoUrl';
import { FitModeRadio } from '../components/FitModeRadio';
import { toast, describeApiError } from '../components/toast';
import { cn } from '../utils/cn';
import { MediaSkeleton } from '../components/skeleton/layouts/MediaSkeleton';
import { MediaFitConfirmModal, hasResolutionMismatch, type MediaFitModalMedia } from '../components/MediaFitConfirmModal';
import { useContentScopeAccess, writableTargetCount } from '../components/useContentScopeAccess';
import { groupSections, visibleContent, type ScopeGroup } from '../components/groupSections';
import { GroupSectionLabel, useCollapsedSections } from '../components/GroupSectionLabel';
import { ScopeSectionsContext } from '../components/ScopeSectionsContext';
import { MediaEditModal } from '../components/MediaEditModal';
import { NoticeEditModal } from '../components/NoticeEditModal';
import { useDeviceGroupsStore } from '../store/deviceGroups';
import { useWriteGate } from '../nav/useWriteGate';
import { NO_GROUP_TITLES, NoAssignedGroupsNotice, hasNoAssignedGroups } from '../components/GroupAssignmentNotice';

/** What the bottom library is adding to the active playlist. */
export type LibraryPick =
  | { kind: 'media'; item: MediaItem }
  | { kind: 'notice'; item: Notice };

export function LibraryLoadErrors({
  playlistError,
  noticesError,
  scheduleError,
  onRetryPlaylists,
  onRetryNotices,
  onRetrySchedule,
}: {
  playlistError: string | null;
  noticesError: string | null;
  scheduleError: string | null;
  onRetryPlaylists: () => void;
  onRetryNotices: () => void;
  onRetrySchedule: () => void;
}) {
  const errors = [
    { key: 'playlists', label: '플레이리스트', message: playlistError, retry: onRetryPlaylists },
    { key: 'notices', label: '슬라이드', message: noticesError, retry: onRetryNotices },
    { key: 'schedule', label: '스케줄 정보', message: scheduleError, retry: onRetrySchedule },
  ].filter((item): item is typeof item & { message: string } => Boolean(item.message));

  if (errors.length === 0) return null;
  return (
    <div className="flex flex-col gap-2" aria-label="라이브러리 조회 오류">
      {errors.map((error) => (
        <div key={error.key} role="alert" className="flex items-center gap-3 rounded border border-red/40 bg-red/10 px-3 py-2 text-[13px] text-red">
          <span className="min-w-0 flex-1">{error.label}을(를) 불러오지 못했습니다. {error.message}</span>
          <Button className="!h-7 !py-0 !text-[12px]" onClick={error.retry}>{error.label} 다시 시도</Button>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Media page — v2.1 layout:
//   [Playlist Sidebar] | [Editor] | [Persistent Library Drawer]
// =============================================================================

const DEFAULT_IMAGE_DURATION = 10;
const SCHEDULE_TIMEZONE = 'Asia/Seoul';
// Fit 데이터/Player 동작은 유지하되 운영 UI만 임시 비노출한다.
// true로 되돌리면 해상도 불일치 팝업과 항목별 변경 버튼이 다시 표시된다.
const MEDIA_FIT_CONTROLS_VISIBLE = false;

// 목업 가이드 — 타입 필터는 전체/비디오/이미지만. WebM은 비디오에 포함.
type MediaFilter = 'all' | 'image' | 'video';
/** 슬라이드 탭의 타입 필터 — 대지 방향(가로/세로)과 템플릿 여부(2026-09-16 대교: 라이브러리와 같은 도구줄). */
type NoticeFilter = 'all' | 'landscape' | 'portrait';
function noticeMatchesFilter(notice: Notice, filter: NoticeFilter): boolean {
  if (filter === 'all') return true;
  const portrait = notice.canvasHeight > notice.canvasWidth;
  return filter === 'portrait' ? portrait : !portrait;
}
function noticeMatchesSearch(notice: Notice, query: string): boolean {
  const q = query.trim().toLowerCase();
  return !q || notice.name.toLowerCase().includes(q);
}
type MediaSort = 'oldest' | 'newest' | 'name' | 'type' | 'duration';
type MediaView = 'grid' | 'list';

// 템플릿 판별은 shared(isTemplateNotice)와 공유 — 신규 조직 시드가 같은 규칙을 쓴다.
const isBrickBriefTemplateNotice = isTemplateNotice;

function isWebmMedia(media: MediaItem): boolean {
  return media.mediaType === 'video'
    && (media.mimeType === 'video/webm' || /\.webm$/i.test(media.originalName));
}

function mediaMatchesFilter(media: MediaItem, filter: MediaFilter): boolean {
  if (filter === 'all') return true;
  // '비디오'는 WebM 포함(가이드). '이미지'는 이미지만.
  return media.mediaType === filter;
}

function mediaTypeLabel(media: MediaItem): string {
  return isWebmMedia(media) ? 'webm' : media.mediaType;
}

/**
 * NBL_REQ_407 — name-based library search. Case-insensitive substring match on
 * the asset's display name (originalName, falling back to filename). An empty /
 * whitespace query matches everything so it composes with the existing type
 * filter + sort. Operates purely on the already org-scoped `mediaList` the
 * server returns, so it never widens visibility beyond the caller's org.
 */
export function mediaMatchesSearch(media: MediaItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const name = (media.originalName || media.filename || '').toLowerCase();
  return name.includes(q);
}

function getActiveTimelinePlaylistId(
  scheduleTypes: ScheduleType[],
  timelineBlocks: TimelineBlock[],
  now: Date,
): string | null {
  const { weekday, minuteOfDay } = getSeoulScheduleTime(now);
  const matches = scheduleTypes.filter((type) => type.enabled && type.weekdays.includes(weekday));
  const activeType = matches.find((type) => !type.builtin) ?? matches[0] ?? null;
  if (!activeType) return null;

  const block = timelineBlocks.find(
    (item) => item.scheduleTypeId === activeType.id
      && minuteOfDay >= item.startMinute
      && minuteOfDay < item.endMinute,
  );
  return block?.playlistId ?? null;
}

function getSeoulScheduleTime(date: Date): { weekday: number; minuteOfDay: number } {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: SCHEDULE_TIMEZONE,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const parts: Record<string, string> = {};
  for (const part of fmt.formatToParts(date)) parts[part.type] = part.value;
  const weekdayMap: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  const hour = Number(parts['hour']) % 24;
  const minute = Number(parts['minute']);
  return {
    weekday: weekdayMap[parts['weekday'] ?? 'Sun'] ?? 0,
    minuteOfDay: hour * 60 + minute,
  };
}

// 플레이리스트 정렬 모드 (BRICK-36~38). "생성 순"은 오래된 항목이 위 —
// 설계서 기대값이자 라벨의 문자 그대로의 의미.
type SortMode = 'created' | 'updated' | 'duration';
const SORT_OPTIONS: ReadonlyArray<{ value: SortMode; label: string }> = [
  { value: 'created', label: '생성 순' },
  { value: 'updated', label: '최근 업데이트 순' },
  { value: 'duration', label: '재생 시간 순' },
];

// 라이브러리 패널 정렬 메뉴 (BRICK-60/61). 슬라이드 탭도 같은 옵션을 쓴다
// (BRICK-74~76).
const MEDIA_SORT_OPTIONS: ReadonlyArray<{ value: MediaSort; label: string }> = [
  { value: 'oldest', label: '생성 순' },
  { value: 'newest', label: '최근 업데이트 순' },
  { value: 'duration', label: '재생 시간 순' },
];

function playlistDuration(p: Playlist): number {
  return p.items.reduce((sum, it) => sum + (it.durationSec ?? 0), 0);
}

// "00분 00초" — 플레이리스트/아이템 총 재생시간 표기 (Figma UX).
function formatKoDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  return `${String(m).padStart(2, '0')}분 ${String(s).padStart(2, '0')}초`;
}

export function sortPlaylists(list: Playlist[], mode: SortMode): Playlist[] {
  const arr = [...list];
  if (mode === 'duration') arr.sort((a, b) => playlistDuration(b) - playlistDuration(a));
  else if (mode === 'updated')
    arr.sort((a, b) => (b.updatedAt ?? '').localeCompare(a.updatedAt ?? ''));
  // 생성 순 — 오래된 항목이 위(BRICK-36).
  else arr.sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));
  return arr;
}


/** 만들기 게이트 문구 — 넣을 자리(내 자료·담당 그룹)가 없으면 만들 수 없다(2026-09-16 대교 지적 1). */
const NO_WRITE_TARGET_REASON = '콘텐츠를 만들 수 있는 그룹이 없습니다 — 조직 관리자에게 담당 그룹을 요청하세요.';

// 2026-09-16 검수 C2 — .tk-tag는 globals.css에서 10px/text-dimmer라 이 화면의 하한(12px)에 못 미친다.
// 전역 클래스는 다른 화면과 함께 손봐야 해서 여기서는 유틸리티로 덮어 쓴다(globals.css는 별도 담당).
const TAG_CLS = 'tk-tag';
const REMOTE_CHANGED_SAVE_REASON = '다른 관리자가 먼저 저장했습니다. 새로고침해 최신 내용을 확인한 뒤 다시 저장해 주세요.';

// 좌측 플레이리스트 드로어 접기 상태(2026-09-16 검수 C12).
// 1024에서 278px 고정 드로어 때문에 가운데 편집기가 ~317px까지 눌렸다. 접을 수 있게 하되
// 토글 버튼은 펼침/접힘 어느 쪽에서도 드로어 머리의 같은 자리(왼쪽 끝)에 있는다 — "조작부는 움직이지 않는다".
const SIDEBAR_COLLAPSED_KEY = 'cornerstone:playlist-sidebar-collapsed';
const SIDEBAR_AUTO_COLLAPSE_W = 1024;

function usePlaylistSidebarCollapsed(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsedState] = useState(() => {
    const saved = window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    // 저장된 선택이 없으면 좁은 화면에서만 접은 채 시작한다.
    if (saved === null) return window.innerWidth <= SIDEBAR_AUTO_COLLAPSE_W;
    return saved === '1';
  });
  const setCollapsed = (next: boolean) => {
    setCollapsedState(next);
    window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
  };
  return [collapsed, setCollapsed];
}

export function Playlists() {
  const access = useContentScopeAccess();
  // SCDS-94 — 권한 정보(/api/device-groups)를 읽는 동안에는 access.options 가 비어 '담당 그룹 없음'
  // 판정이 풀린다. 그 사이 일반 플레이리스트 화면이 한 번 그려졌다가 안내로 돌아가 깜박였다
  // (진입할 때, [다시 확인]을 누를 때 둘 다). 첫 판정 전에는 제목과 한 줄 안내만 보이고, 다시 읽는
  // 동안에는 마지막 판정을 그대로 유지한다.
  const scopeSettledRef = useRef(false);
  const wasUnassignedRef = useRef(false);
  if (access.options) {
    scopeSettledRef.current = true;
    wasUnassignedRef.current = hasNoAssignedGroups(access.options.actor);
  }
  const checkingScope = !access.options && !access.error;
  // BO 대행 '읽기 전용'이면 쓰기 컨트롤은 숨기지 않고 비활성 + 이유(검수 P1).
  const write = useWriteGate();
  // 권한이 있어도 넣을 자리(내 자료·담당 그룹)가 없으면 만들 수 없다(2026-09-16 대교 지적 1).
  // 숨기지 않고 같은 자리에 비활성 + 이유 — "조작부는 움직이지 않는다".
  const createGate = write
    .and(access.canCreate, '이 계정에는 콘텐츠 생성 권한이 없습니다.')
    .and(access.writeTargets > 0, NO_WRITE_TARGET_REASON);
  const canCreateHere = access.canCreate && access.writeTargets > 0;
  const [sidebarCollapsed, setSidebarCollapsed] = usePlaylistSidebarCollapsed();
  const playlists = useLibraryStore((s) => s.playlists);
  const playlistError = useLibraryStore((s) => s.playlistError);
  const loadPlaylists = useLibraryStore((s) => s.loadPlaylists);
  const scheduleTypes = useLibraryStore((s) => s.scheduleTypes);
  const scheduleError = useLibraryStore((s) => s.scheduleError);
  const loadScheduleTypes = useLibraryStore((s) => s.loadScheduleTypes);
  const timelineBlocks = useLibraryStore((s) => s.timelineBlocks);
  const loadTimelineBlocks = useLibraryStore((s) => s.loadTimelineBlocks);
  const createPlaylist = useLibraryStore((s) => s.createPlaylist);
  const updatePlaylist = useLibraryStore((s) => s.updatePlaylist);
  const deletePlaylist = useLibraryStore((s) => s.deletePlaylist);
  const [listSort, setListSort] = useState<SortMode>('created');
  // Figma UX — 추가/수정은 미니 모달로 처리. 폴더 관련 모달은 2026-09-16 대교 결정으로 전부 사라졌다.
  const [playlistModal, setPlaylistModal] = useState<
    { mode: 'create' } | { mode: 'edit'; playlist: Playlist } | null
  >(null);
  // 삭제 확인 팝업(공용 ConfirmModal). window.confirm 대체(UX 컨벤션).
  const [deleteConfirm, setDeleteConfirm] = useState<Playlist | null>(null);
  // 미디어·슬라이드와 같은 규칙 — 확인 모달이 열릴 때 사용처를 미리 물어보고 삭제를 막는다.
  const playlistUsage = useContentUsage('playlist', deleteConfirm?.id ?? null);

  const mediaList = useCalibrationStore((s) => s.mediaList);
  const loadMedia = useCalibrationStore((s) => s.loadMedia);
  const playbackSource = useCalibrationStore((s) => s.playbackSource);
  const playbackMode = useCalibrationStore((s) => s.playbackMode);
  const setPlaybackSource = useCalibrationStore((s) => s.setPlaybackSource);
  const setPlaybackState = useCalibrationStore((s) => s.setPlaybackState);
  const setPlaybackMode = useCalibrationStore((s) => s.setPlaybackMode);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 모바일(768px 미만) — 한 화면에 한 가지 일. 목록에서 고르면 편집 화면으로 넘어가고(뒤로 가기),
  // 라이브러리는 위쪽 전환으로 따로 연다. 데스크톱은 세 칸 그대로(2026-09-23).
  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState<'playlists' | 'library'>('playlists');
  const [mobileEditing, setMobileEditing] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<LibraryPick | null>(null);
  /** 방금 지운 콘텐츠 — 편집 중인(저장 전) 플레이리스트 초안에서도 빼야 한다. 저장 전 항목은
   *  playlist_items 에 없어 서버의 '사용 중' 검사에 안 걸리므로, 지우고 나면 초안에 없는 것을
   *  가리키는 줄이 '(missing)'으로 남았다(2026-09-17 대교). 남길 게 아니라 빼는 게 맞다. */
  const [pendingRemove, setPendingRemove] = useState<{ kind: 'media' | 'notice'; id: string } | null>(null);
  const [scheduleNow, setScheduleNow] = useState(() => new Date());
  const [isLoading, setIsLoading] = useState(true);
  const libraryWidth = useLibraryDrawerWidth();

  const notices = useLibraryStore((s) => s.notices);
  const noticesError = useLibraryStore((s) => s.noticesError);
  const loadNotices = useLibraryStore((s) => s.loadNotices);
  const deleteNotice = useLibraryStore((s) => s.deleteNotice);
  const bulkDeleteNotices = useLibraryStore((s) => s.bulkDeleteNotices);

  useEffect(() => {
    setIsLoading(true);
    void Promise.all([
      loadPlaylists(),
      loadScheduleTypes(),
      loadTimelineBlocks(),
      loadMedia(),
      loadNotices(),
    ]).finally(() => setIsLoading(false));
  }, [loadPlaylists, loadScheduleTypes, loadTimelineBlocks, loadMedia, loadNotices, setPlaybackMode, setPlaybackSource, setPlaybackState]);

  // '편성 중' 아이콘은 서버가 목록을 줄 때 계산한다 — 화면을 띄워 둔 채 편성이 끝나면 아이콘이 남았다(SCDS-110).
  // 화면이 보이는 동안 1분마다, 탭으로 돌아오면 바로 다시 받는다. 편집 중인 초안은 같은 저장본이면 덮지 않는다.
  useEffect(() => {
    const refresh = (): void => { if (document.visibilityState === 'visible') void Promise.all([loadPlaylists(), loadMedia(), loadNotices()]); };
    const timer = window.setInterval(refresh, 60_000);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', refresh); };
  }, [loadPlaylists, loadMedia, loadNotices]);

  const retryScheduleData = useCallback(() => {
    useLibraryStore.setState({ scheduleError: null });
    void Promise.all([loadScheduleTypes(), loadTimelineBlocks()]);
  }, [loadScheduleTypes, loadTimelineBlocks]);

  useEffect(() => {
    const actor = access.options?.actor;
    if (!actor || (actor.enforced && !actor.central)) return;
    let active = true;
    void api.getState().then(state => {
      if (!active) return;
      if (state.playbackSource) setPlaybackSource(state.playbackSource);
      if (state.playbackState) setPlaybackState(state.playbackState);
      if (state.playbackMode) setPlaybackMode(state.playbackMode);
    }).catch(() => undefined);
    return () => { active = false; };
  }, [access.options?.actor, setPlaybackMode, setPlaybackSource, setPlaybackState]);

  useEffect(() => {
    const timer = window.setInterval(() => setScheduleNow(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const scheduledPlaylistId = useMemo(
    () => getActiveTimelinePlaylistId(scheduleTypes, timelineBlocks, scheduleNow),
    [scheduleTypes, timelineBlocks, scheduleNow],
  );
  const activePlaylistId = playbackSource.kind === 'playlist'
    ? playbackSource.playlistId ?? null
    : playbackMode === 'schedule'
      ? scheduledPlaylistId
      : null;
  const scopeGroups = access.options?.groups ?? [];
  const scopeCentral = access.options?.actor.central ?? false;
  const sortedPlaylists = useMemo(
    () => sortPlaylists(playlists, listSort),
    [playlists, listSort],
  );
  // 이 사람이 실제로 볼 수 있는 것만 — 목록·머리 숫자가 같은 배열을 본다(2026-09-16 대교 지적 3).
  const scopedPlaylists = useMemo(
    () => visibleContent(sortedPlaylists, scopeGroups, scopeCentral),
    [sortedPlaylists, scopeGroups, scopeCentral],
  );
  // 소유 그룹 묶음. 볼 수 있는 묶음이 하나뿐이면 null → 머리 없는 평평한 목록(2026-09-16 대교).
  const playlistSections = useMemo(
    () => groupSections(scopedPlaylists, scopeGroups, scopeCentral),
    [scopedPlaylists, scopeGroups, scopeCentral],
  );
  const playlistFold = useCollapsedSections('playlists');
  // 편집기에 여는 것도 볼 수 있는 목록 안에서만 — 격리된 플레이리스트가 자동 선택으로 열리면 안 된다(2026-09-16).
  const selected = useMemo(
    () => scopedPlaylists.find((p) => p.id === selectedId) ?? null,
    [scopedPlaylists, selectedId],
  );
  const autoSelectedPlaylistId = activePlaylistId ?? scopedPlaylists[0]?.id ?? null;

  useEffect(() => {
    if (!autoSelectedPlaylistId) return;
    if (!scopedPlaylists.some((playlist) => playlist.id === autoSelectedPlaylistId)) return;
    setSelectedId((current) => {
      if (current && scopedPlaylists.some((playlist) => playlist.id === current)) return current;
      return autoSelectedPlaylistId;
    });
  }, [autoSelectedPlaylistId, scopedPlaylists]);

  // 깊숙한 자식(슬라이드 편집기의 템플릿 갤러리)이 그룹 묶음을 그릴 수 있게 스코프 정보를 내려 준다(2026-09-15).
  // 훅이라 아래 스켈레톤 early return보다 위에 있어야 한다.
  const scopeSections = useMemo(() => ({ groups: scopeGroups, central: scopeCentral }), [scopeGroups, scopeCentral]);

  // 담당 그룹이 아직 없는 계정에게는 "불러오지 못했습니다"가 아니라 배정 안내를 보인다.
  // 실패가 아니라 아직 배정되지 않은 상태다(2026-09-17 대교).
  // SCDS-94 재검수 — 스켈레톤도 플레이리스트 화면을 그대로 흉내 내서, 스켈레톤 → 안내로 바뀌는 것 자체가
  // 깜박임으로 보였다. 그래서 이 판정을 목록 로딩(isLoading)보다 먼저 하고, 권한을 확인하는 동안에는
  // 스케줄 화면처럼 제목과 한 줄 안내만 둔다. 안내와 같은 틀이라 판정이 나도 제목 자리가 움직이지 않는다.
  const unassigned = hasNoAssignedGroups(access.options?.actor) || (checkingScope && wasUnassignedRef.current);
  if (unassigned || (checkingScope && !scopeSettledRef.current)) {
    return (
      <PageContainer variant="full" className="flex flex-col gap-4 bg-bg px-6 pb-6">
        <div className="flex-none pt-6"><PageHeader items={[{ label: '플레이리스트' }]} /></div>
        {unassigned
          ? <NoAssignedGroupsNotice title={NO_GROUP_TITLES.content} onRetry={access.reload} showWorkspaceLink />
          : <p role="status" className="text-[13px] text-text-dim">권한을 확인하는 중…</p>}
      </PageContainer>
    );
  }

  if (isLoading) {
    return (
      <PageContainer variant="full" className="flex bg-bg overflow-hidden">
        <MediaSkeleton />
      </PageContainer>
    );
  }

  // 행 렌더는 그룹 묶음마다 다시 쓰이므로 함수로 뺐다(2026-09-15 A안).
  // 폴더 렌더(renderPlaylistFolder)는 2026-09-16 대교 결정으로 없앴다 — 폴더 개념 자체가 사라졌다.
  const renderPlaylistRow = (p: Playlist) => (
              <PlaylistRow
                key={p.id}
                playlist={p}
                active={p.id === selectedId}
                playing={p.id === activePlaylistId}
                onSelect={() => { setSelectedId(p.id); setMobileEditing(true); }}
                onDelete={() => setDeleteConfirm(p)}
              />
  );

  return (
    <ScopeSectionsContext.Provider value={scopeSections}>
    <PageContainer variant="full" className={isMobile ? 'flex flex-col bg-bg overflow-hidden px-3 pb-3' : 'flex gap-4 bg-bg overflow-hidden px-6 pb-6'}>
      <div className={cn('flex min-w-0 flex-1 flex-col overflow-hidden', isMobile ? 'gap-3' : 'gap-4', isMobile && mobileTab === 'library' && 'hidden')}>
        <div className={cn('flex-none', isMobile ? 'pt-3' : 'pt-6')}>
          {isMobile
            ? <PageHeader items={[{ label: '플레이리스트' }]} className="!mb-2" actions={<MobilePaneSwitch value={mobileTab} onChange={setMobileTab} />} />
            : <PageHeader items={[{ label: '플레이리스트' }]} />}
          {access.error && <div role="alert" className="text-[13px] text-red">{access.error} <Button className="!text-[13px]" onClick={access.reload}>권한 다시 불러오기</Button></div>}
          {!access.options && !access.error && <p role="status" className="text-[13px] text-text-dim">콘텐츠 생성 권한 확인 중…</p>}
          {access.options?.actor.enforced && !access.canCreate && <p className="text-[13px] text-text-dim">이 계정에는 콘텐츠 생성 권한이 없습니다.</p>}
          <LibraryLoadErrors
            playlistError={playlistError}
            noticesError={noticesError}
            scheduleError={scheduleError}
            onRetryPlaylists={() => void loadPlaylists()}
            onRetryNotices={() => void loadNotices()}
            onRetrySchedule={retryScheduleData}
          />
        </div>
        <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
      <aside className={cn('flex flex-none flex-col min-h-0 overflow-hidden rounded-md border border-line bg-panel',
        isMobile ? (mobileEditing && selected ? 'hidden' : 'w-full') : sidebarCollapsed ? 'w-[52px]' : 'w-[278px]')}>
        {/* 토글은 머리의 맨 왼쪽 고정 — 접어도 x가 그대로라 버튼을 다시 찾을 필요가 없다(검수 C12). */}
        <header className="flex min-w-0 items-center gap-2 whitespace-nowrap border-b border-line px-3 py-3">
          {!isMobile && <button
            type="button"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? '플레이리스트 목록 펼치기' : '플레이리스트 목록 접기'}
            aria-label={sidebarCollapsed ? '플레이리스트 목록 펼치기' : '플레이리스트 목록 접기'}
            className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-sm border border-line bg-panel text-[13px] text-text-dim hover:border-line-strong hover:text-text"
          >
            {sidebarCollapsed ? '»' : '«'}
          </button>}
          {(isMobile || !sidebarCollapsed) && <>
            <CtrlLabel className="min-w-0 truncate">플레이리스트</CtrlLabel>
            {/* 머리 숫자도 목록과 같은 배열의 길이 — 조직 전체 수를 보여 주면 어긋난다(2026-09-16 대교 지적 3). */}
            <span className="font-mono text-[12px] text-text-dim">{scopedPlaylists.length}</span>
            <div className="ml-auto flex flex-none items-center gap-1">
              {/* 권한이 없어도 숨기지 않는다 — 같은 자리에 비활성 + 이유(검수 S12·P1). */}
              {/* 고를 것이 하나뿐인 메뉴는 한 단계를 헛되이 만든다 — 곧바로 만들기 모달(2026-09-17 대교). */}
              <ToolbarButton glyph="＋" className="flex-none" disabled={createGate.blocked}
                title={createGate.reason ?? '플레이리스트 만들기'} onClick={() => setPlaylistModal({ mode: 'create' })} />
              <ToolbarMenu glyph="≡" title="정렬" className="flex-none" align="right">
                {SORT_OPTIONS.map((o) => (
                  <MenuRow
                    key={o.value}
                    label={o.label}
                    checked={listSort === o.value}
                    onClick={() => setListSort(o.value)}
                  />
                ))}
              </ToolbarMenu>
            </div>
          </>}
        </header>
        {/* 폴더 드래그앤드롭(끌어서 폴더에 넣기)은 2026-09-16 대교 결정으로 없앴다 — 폴더가 없으니 옮길 곳도 없다. */}
        <div
          className={cn('flex-1 min-h-0 overflow-auto p-2 flex flex-col gap-1.5', sidebarCollapsed && !isMobile && 'hidden')}
        >
            {scopedPlaylists.length === 0 && !playlistError && !playlistSections && (
              // 빈 목록은 문구만 둔다 — 만들기는 머리 ＋와 오른쪽 콘텐츠 영역 버튼(SCDS-96 재검수). 그룹
              // 묶음이 있으면 이 문구 대신 묶음 목록이 빈 상태를 보여 준다(그룹마다 "콘텐츠가 없습니다.").
              <div className="flex flex-col items-center gap-1.5 py-8 text-center">
                <p className="font-mono text-[13px] text-text-dim">추가된 플레이리스트가 없습니다.</p>
                {createGate.reason && <p className="text-[12px] text-text-faint">{createGate.reason}</p>}
              </div>
            )}
            {playlistSections ? playlistSections.map((section) => (
              <div key={section.key} className="flex flex-col gap-1.5" data-group-section={section.key}>
                <GroupSectionLabel label={section.label} central={section.central} empty={section.items.length === 0}
                  count={section.items.length} collapsed={playlistFold.isCollapsed(section.key)} onToggle={() => playlistFold.toggle(section.key)} />
                {!playlistFold.isCollapsed(section.key) && section.items.map(renderPlaylistRow)}
              </div>
            )) : scopedPlaylists.map(renderPlaylistRow)}
        </div>
      </aside>

      <section className={cn('flex-1 min-w-0 min-h-0 flex flex-col border border-line rounded-md bg-panel overflow-hidden',
        isMobile && !(mobileEditing && selected) && 'hidden')}>
        {/* 구성 관리 그룹 머리와 같은 꼴 — 작은 라벨 · 큰 제목 · 정보 태그(해상도·아이템 수·공개 범위) · 우측 [수정] (2026-09-15 대교). */}
        <header className={cn('flex flex-none flex-wrap items-center border-b border-line bg-bg py-3', isMobile ? 'gap-2 px-3' : 'gap-3 px-[22px]')}>
          {isMobile
            ? <button type="button" onClick={() => setMobileEditing(false)}
                className="-ml-1 inline-flex h-9 items-center gap-1 rounded px-2 text-[14px] text-text-dim hover:text-text">‹ 목록</button>
            : <CtrlLabel className="text-accent">플레이리스트</CtrlLabel>}
          <h1 className="min-w-0 truncate text-[19px] font-semibold tracking-[-0.01em]">
            {selected?.name ?? ''}
          </h1>
          {selected && (
            <span className={TAG_CLS}>
              {selected.resolutionWidth}×{selected.resolutionHeight} · 아이템 {selected.items.length}개
            </span>
          )}
          {selected && (
            <div className="ml-auto flex items-center gap-2">
              {isMobile && selected.canWrite !== false && (
                <Button variant="primary" onClick={() => setMobileTab('library')}>＋ 항목 추가</Button>
              )}
              {/* 권한이 없어도 자리는 지키고 이유를 단다(검수 S1·S11). */}
              <Button
                {...write.and(selected.canWrite !== false, selected.writeBlockedReason ?? '읽기 전용 플레이리스트입니다').props}
                onClick={() => setPlaylistModal({ mode: 'edit', playlist: selected })}
              >수정</Button>
            </div>
          )}
        </header>
        <main className={cn('flex-1 min-h-0 overflow-auto', isMobile ? 'p-3' : 'p-[22px]')}>
            {selected ? (
              <PlaylistEditor
                key={selected.id}
                playlist={selected}
                canCreate={access.canCreate}
                mediaList={mediaList}
                notices={notices}
                pendingAdd={pendingAdd}
                onConsumeAdd={() => setPendingAdd(null)}
                pendingRemove={pendingRemove}
                onConsumeRemove={() => setPendingRemove(null)}
                onSave={(patch) => selected.canWrite === false ? Promise.reject(new Error(selected.writeBlockedReason ?? '읽기 전용 플레이리스트입니다.')) : updatePlaylist(selected.id, patch)}
                onPreview={() => window.open(playlistPreviewUrl(selected.id), '_blank', 'noopener')}
              />
            ) : playlistError && scopedPlaylists.length === 0 ? null : (
              // 첫 플레이리스트는 여기서도 만든다 — 없애는 건 목록 빈 칸의 버튼뿐(SCDS-96 재검수).
              // 하나라도 생기면 편집기가 이 자리를 차지하므로 버튼은 저절로 사라진다.
              <div className="h-full flex flex-col items-center justify-center gap-1.5 text-center">
                <p className="font-mono text-[14px] text-text-dim">
                  {scopedPlaylists.length === 0 ? '아직 플레이리스트가 없습니다.' : '플레이리스트를 선택해주세요.'}
                </p>
                {scopedPlaylists.length === 0 && (
                  <Button variant="primary" className="mt-1.5" {...createGate.props}
                    onClick={() => setPlaylistModal({ mode: 'create' })}>＋ 플레이리스트 만들기</Button>
                )}
              </div>
            )}
        </main>
      </section>
        </div>
      </div>

      {!isMobile && <LibraryDrawerResizeHandle width={libraryWidth.width} onWidthChange={libraryWidth.setWidth} onPointerDown={libraryWidth.onPointerDown} />}
      {isMobile && mobileTab === 'library' && (
        <div className="flex flex-none items-center gap-2 pb-2 pt-3">
          {mobileEditing && selected ? (
            <>
              <button type="button" onClick={() => setMobileTab('playlists')}
                className="-ml-1 inline-flex h-9 flex-none items-center gap-1 rounded px-2 text-[14px] text-text-dim hover:text-text">‹ 편집으로</button>
              <p className="min-w-0 truncate text-[13px] text-text-dim"><span className="font-semibold text-text">{selected.name}</span>에 추가</p>
            </>
          ) : <h1 className="text-[22px] font-bold leading-9 tracking-[-0.01em] text-text">플레이리스트</h1>}
          <div className="ml-auto flex-none"><MobilePaneSwitch value={mobileTab} onChange={setMobileTab} /></div>
        </div>
      )}
      <aside
        // flex-none 과 flex-1 을 함께 두면 flex-none 이 이겨 칸이 내용 길이만큼 늘어나고, 안쪽 목록이
        // 스크롤되지 않는다(2026-09-23 모바일 보고). 모바일은 남은 높이를 채우는 flex-1 만.
        className={cn('min-h-0 border border-line rounded-md bg-panel overflow-hidden',
          isMobile ? (mobileTab === 'library' ? 'flex-1' : 'hidden') : 'flex-none mt-4')}
        style={isMobile ? undefined : { width: libraryWidth.width }}
      >
        <MediaLibraryPanel
          mediaList={mediaList}
          notices={notices}
          noticesError={noticesError}
          selectedPlaylistId={selectedId}
          selectedPlaylistReadOnly={selected?.canWrite === false}
          canCreate={canCreateHere}
          legacyPlayback={access.options?.actor.enforced === false}
          selectedPlaylistGroupId={selected?.ownerGroupId ?? null}
          onAddToPlaylist={(pick) => { if (selected?.canWrite !== false) setPendingAdd(pick); }}
          onContentDeleted={(kind, id) => setPendingRemove({ kind, id })}
          onDeleteNotice={deleteNotice}
          onBulkDeleteNotices={(body) => bulkDeleteNotices(body)}
          scopeGroups={scopeGroups}
          scopeCentral={scopeCentral}
          scopeActor={access.options?.actor}
          isOwner={access.isOwner}
          createGroups={access.ownableGroups}
          canChooseCreateGroup={access.canChooseOwnerGroup}
          canChooseCommonGroup={access.canChooseCommonGroup}
        />
      </aside>

      {playlistModal && (playlistModal.mode === 'create' ? canCreateHere : playlists.find((p) => p.id === playlistModal.playlist.id)?.canWrite !== false) && (
        <PlaylistFormModal
          mode={playlistModal.mode}
          playlist={playlistModal.mode === 'edit' ? playlistModal.playlist : undefined}
          existingNames={playlists
            .filter((p) =>
              playlistModal.mode === 'edit' ? p.id !== playlistModal.playlist.id : true,
            )
            .map((p) => p.name.trim().toLowerCase())}
          // 만들 때와 옮길 때의 권한이 다르다. 만드는 건 자기 담당 그룹이면 되지만(서버 permits),
          // 이미 있는 것을 다른 그룹으로 옮기는 소유 이전은 조직 Owner만 한다(groupScope.classify).
          // 그래서 여러 그룹을 맡은 구성원에게는 '만들기'에서만 칸을 연다(2026-09-18 대교).
          // 옮기기 칸도 서버와 같은 기준 — 조직 Owner, 또는 미분류를 처음 분류하는 중앙 관리자(SCDS-93).
          canChooseGroup={playlistModal.mode === 'create'
            ? access.canChooseOwnerGroup
            : access.isOwner || (!!access.options?.actor.central && playlistModal.playlist.scopeClassified === false)}
          ownableGroups={access.ownableGroups}
          allowCommonGroup={access.canChooseCommonGroup}
          onClose={() => setPlaylistModal(null)}
          onSubmit={async ({ name, resolutionWidth, resolutionHeight, ownerGroupId }) => {
            if (playlistModal.mode === 'create') {
              if (!canCreateHere) throw new Error('콘텐츠 생성 권한이 없습니다.');
              // folderId는 더 이상 보내지 않는다 — 폴더 개념이 화면에서 빠졌다(2026-09-16 대교).
              const created = await createPlaylist({
                name,
                resolutionWidth,
                resolutionHeight,
                ...(ownerGroupId !== undefined ? { ownerGroupId } : {}),
              });
              setSelectedId(created.id);
              toast('추가되었습니다.', 'success');
            } else {
              const current = playlists.find((p) => p.id === playlistModal.playlist.id) ?? playlistModal.playlist;
              if (current.canWrite === false) throw new Error(current.writeBlockedReason ?? '읽기 전용 플레이리스트입니다.');
              // 소유 그룹은 revision 검사가 있어 다른 저장보다 먼저(어떤 UPDATE든 revision을 올린다).
              const moved = ownerGroupId === undefined ? false : await applyOwnerGroup('playlist', current, ownerGroupId);
              await updatePlaylist(playlistModal.playlist.id, {
                name,
                resolutionWidth,
                resolutionHeight,
              });
              if (moved) await loadPlaylists();
              toast('저장되었습니다.', 'success');
            }
          }}
        />
      )}
      {deleteConfirm && (
        <ConfirmModal
          title="플레이리스트 삭제"
          message={<>
            {`'${deleteConfirm.name}' 플레이리스트를 삭제하시겠습니까?\n삭제한 내용은 복구할 수 없습니다.`}
            {!!playlistUsage.usage?.length && <ContentUsageNotice usage={playlistUsage.usage} noun="플레이리스트" />}
          </>}
          confirmLabel="삭제"
          confirmDisabled={playlistUsage.loading || !!playlistUsage.usage?.length}
          confirmDisabledReason={playlistUsage.loading ? '사용처를 확인하는 중입니다.' : '편성에 걸린 플레이리스트는 지울 수 없습니다.'}
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={async () => {
            if (playlists.find((p) => p.id === deleteConfirm.id)?.canWrite === false) throw new Error('읽기 전용 플레이리스트입니다.');
            const result = await deletePlaylist(deleteConfirm.id);
            if (selectedId === deleteConfirm.id) setSelectedId(null);
            // 다른 그룹 편성이 쓰고 있으면 지우는 대신 조직 관리자 자료로 옮긴다 — 그 사실을 알린다.
            toast(result?.detail ?? '삭제되었습니다.', result ? 'info' : 'success');
          }}
        />
      )}
    </PageContainer>
    </ScopeSectionsContext.Provider>
  );
}

const LIBRARY_WIDTH_KEY = 'cornerstone:media-library-width';
const LIBRARY_MIN_W = 280;
const LIBRARY_MAX_W = 560;

function useLibraryDrawerWidth() {
  const [width, setWidth] = useState(() => {
    const saved = Number(window.localStorage.getItem(LIBRARY_WIDTH_KEY));
    return Number.isFinite(saved)
      ? Math.max(LIBRARY_MIN_W, Math.min(LIBRARY_MAX_W, saved))
      : 312;
  });

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const move = (ev: PointerEvent) => {
      const next = Math.max(
        LIBRARY_MIN_W,
        Math.min(LIBRARY_MAX_W, startW - (ev.clientX - startX)),
      );
      setWidth(next);
    };
    const up = () => {
      window.localStorage.setItem(LIBRARY_WIDTH_KEY, String(widthRef.current));
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const widthRef = useRef(width);
  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  /** 키보드 조절(2026-09-16 검수 C4) — 드래그와 같은 범위로 클램프하고 바로 저장한다. */
  const setWidthClamped = (next: number) => {
    const clamped = Math.max(LIBRARY_MIN_W, Math.min(LIBRARY_MAX_W, Math.round(next)));
    setWidth(clamped);
    window.localStorage.setItem(LIBRARY_WIDTH_KEY, String(clamped));
  };

  return { width, onPointerDown, setWidth: setWidthClamped };
}

// 라이브러리 드로어 폭 조절 손잡이.
/** 모바일에서 플레이리스트와 라이브러리 사이를 오가는 전환 — 두 화면이 한 번에 들어가지 않는다. */
function MobilePaneSwitch({ value, onChange }: { value: 'playlists' | 'library'; onChange: (next: 'playlists' | 'library') => void }) {
  return <Segmented value={value} onChange={onChange}
    options={[{ value: 'playlists', label: '플레이리스트' }, { value: 'library', label: '라이브러리' }]} />;
}

// 2026-09-16 검수 C4 — 마우스 드래그로만 쓸 수 있었다. role=separator에 tabIndex와
// aria-value*를 붙여 현재 폭을 읽히게 하고 ←/→(Shift = 큰 단계) · Home/End로 조절한다.
// 포커스 표시는 다른 컨트롤과 같은 오렌지 ring.
const LIBRARY_STEP_W = 16;
const LIBRARY_BIG_STEP_W = 64;

function LibraryDrawerResizeHandle({
  width,
  onWidthChange,
  onPointerDown,
}: {
  width: number;
  onWidthChange: (next: number) => void;
  onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => void;
}) {
  // 드로어는 오른쪽에 있다 → ←는 넓히기, →는 좁히기(손잡이를 미는 방향과 같다).
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = e.shiftKey ? LIBRARY_BIG_STEP_W : LIBRARY_STEP_W;
    const next =
      e.key === 'ArrowLeft' ? width + step
      : e.key === 'ArrowRight' ? width - step
      : e.key === 'Home' ? LIBRARY_MIN_W
      : e.key === 'End' ? LIBRARY_MAX_W
      : null;
    if (next === null) return;
    e.preventDefault();
    onWidthChange(next);
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="라이브러리 폭 조절"
      aria-valuenow={Math.round(width)}
      aria-valuemin={LIBRARY_MIN_W}
      aria-valuemax={LIBRARY_MAX_W}
      tabIndex={0}
      onKeyDown={onKeyDown}
      title="드래그해서 라이브러리 폭 조절 · ←→ 키로도 조절"
      onPointerDown={onPointerDown}
      className="group relative z-10 -mx-2 -mb-4 w-2 flex-none cursor-col-resize bg-bg outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-line transition-colors group-hover:bg-accent" />
      <div className="absolute top-1/2 left-1/2 h-12 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-line opacity-0 transition-opacity group-hover:opacity-100" />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Playlist row (sidebar entry) — Figma UX:
//   hover ⋮ 메뉴(수정/삭제). '폴더로 이동…'과 폴더 드래그앤드롭은 2026-09-16 대교 결정으로 없앴다.
// -----------------------------------------------------------------------------
function PlaylistRow({
  playlist,
  active,
  playing,
  onSelect,
  onDelete,
}: {
  playlist: Playlist;
  active: boolean;
  playing: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      data-playlist-id={playlist.id}
      className={cn(
        'group flex items-center gap-2 px-3 py-2.5 rounded border cursor-pointer transition-colors',
        active
          ? 'border-accent-ring bg-accent-soft'
          : 'border-transparent bg-transparent hover:border-line hover:bg-panel-hover',
      )}
      onClick={onSelect}
    >
      <div className="flex-1 min-w-0">
        <div className="font-ui text-[15px] font-semibold text-text flex items-center gap-1.5">
          {playing && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-green animate-status-pulse" />}
          {/* 이름만 줄이고 기호는 오른쪽에 고정 — 긴 이름이 기호를 밀어내 잘리던 것(2026-10-01 대교). */}
          <span className="min-w-0 flex-1 truncate">{playlist.name}</span>
          {/* 송출 중 편성에 걸린 것은 아이콘으로 표시한다 — 고치려다 막히기 전에 알 수 있게(SCDS-102, 2026-10-01 대교). */}
          <LiveMarks meta={playlist} what="플레이리스트" />
          {playing && <span className={cn(TAG_CLS, '!text-accent ml-auto')}>NOW</span>}
        </div>
        <div className="font-mono text-[12px] text-text-dim">
          {formatKoDuration(playlistDuration(playlist))}
        </div>
      </div>
      {playlist.canWrite !== false && <span onClick={(e) => e.stopPropagation()}>
        <ToolbarMenu
          glyph="⋮"
          title="더보기"
          align="right"
          className="opacity-0 group-hover:opacity-100 [&[open]]:opacity-100 transition-opacity duration-fast"
        >
          {/* 이름·해상도 수정은 오른쪽 머리의 '수정' 한 곳에서만 연다 — 같은 창이 두 군데서 열려
              헷갈렸다(SCDS-74). 여기엔 목록에서만 할 수 있는 삭제만 둔다. */}
          <MenuRow label="삭제" danger onClick={onDelete} />
        </ToolbarMenu>
      </span>}
    </div>
  );
}

// -----------------------------------------------------------------------------
// 미니 모달 — 플레이리스트 추가/수정 (Figma UX).
// ModalShell은 components/menu.tsx의 공용 셸을 사용한다.
// 폴더 지정 필드는 2026-09-16 대교 결정("폴더 개념 없애자")으로 없앴고, 그 자리에
// 등록된 기기에서 해상도를 골라 채우는 선택지가 들어왔다(같은 날 대교 지적 5).
// 공개/비공개(공개 범위) 라디오도 2026-09-17 대교 지적으로 뺐다 — 폴더·공통 분류를 없애면서
// 화면에서 사라졌어야 할 잔재였다. 소유 그룹은 서버가 정한다.
// -----------------------------------------------------------------------------
function PlaylistFormModal({
  mode,
  playlist,
  existingNames,
  canChooseGroup = false,
  ownableGroups,
  allowCommonGroup = true,
  onClose,
  onSubmit,
}: {
  mode: 'create' | 'edit';
  playlist?: Playlist | undefined;
  /** 중복 검증용 기존 플레이리스트명 (수정 시 자기 자신 제외). 소문자·trim 상태. */
  existingNames: string[];
  /** 소유 그룹을 고를 수 있는가 — 중앙이거나 맡은 그룹이 둘 이상일 때. 한 곳만 맡았으면 고를 것이 없다. */
  canChooseGroup?: boolean;
  /** 고를 수 있는 그룹. 중앙은 전부, 그룹 구성원은 쓰기 권한이 있는 담당 그룹만. */
  ownableGroups?: Array<{ id: string; name: string }>;
  /** '공통(중앙 소유)'을 고를 수 있는가 — 중앙만. */
  allowCommonGroup?: boolean;
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    resolutionWidth: number;
    resolutionHeight: number;
    /** 만들 때만. undefined면 서버 기본값(중앙=공통, 그룹 구성원=담당 첫 그룹). */
    ownerGroupId?: string | null;
  }) => Promise<void>;
}) {
  const [name, setName] = useState(playlist?.name ?? '');
  const [width, setWidth] = useState(playlist ? String(playlist.resolutionWidth) : '');
  const [height, setHeight] = useState(playlist ? String(playlist.resolutionHeight) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 해상도를 기기에서 가져오기 — 이 사람이 볼 수 있는 기기만 서버가 내려 준다(GET /api/device-groups).
  //
  // 예전에는 선택 상태를 두지 않고 '지금 값과 일치하는 기기'를 역으로 찾았다. 그래서 두 가지가
  // 어긋났다(2026-09-17 대교): 기기를 골라 놓고도 폭·높이를 계속 고칠 수 있었고, 반대로 '직접
  // 입력'을 다시 고르면 onChange 가 기기를 못 찾아 그냥 돌아가 버려 빠져나올 수가 없었다.
  // 이제 고른 기기를 상태로 들고, 고른 동안에는 해상도를 참고용으로만 보여 준다.
  /** 고른 기기. 빈 문자열이면 '직접 입력'. */
  const [deviceId, setDeviceId] = useState('');
  /** 소유 그룹. '' = 공통(중앙 소유). 조직 Owner·중앙 관리자만 고른다. */
  const initialOwnerGroup = playlist ? ownerGroupOf(playlist) : '';
  // SCDS-97 — 공통을 고를 수 없는 사람(담당 그룹이 둘 이상인 그룹 관리자·편집자)은 첫 담당 그룹에서
  // 시작한다. 예전에는 '' (공통)에서 시작해, 칸에는 첫 그룹이 보이는데 실제로는 공통(null)으로
  // 보내져 서버가 403(content write permission required)으로 막았다.
  const commonFallback = allowCommonGroup ? '' : ownableGroups?.[0]?.id ?? '';
  const [ownerGroupId, setOwnerGroupId] = useState(
    mode === 'create' && !initialOwnerGroup ? commonFallback : initialOwnerGroup ?? '');
  const groups = useDeviceGroupsStore((s) => s.groups);
  const devices = useDeviceGroupsStore((s) => s.devices);
  const loadDevices = useDeviceGroupsStore((s) => s.load);
  useEffect(() => { void loadDevices(); }, [loadDevices]);
  const sizedDevices = useMemo(
    () => devices.filter((device) => device.resolutionW !== null && device.resolutionH !== null),
    [devices],
  );

  const trimmed = name.trim();
  const duplicate =
    trimmed.length > 0 && existingNames.includes(trimmed.toLowerCase());
  // BRICK-30/33 — 이름·해상도 필수. 예전에는 빈 해상도를 조용히
  // 1920x1080 으로 바꿔 넣어 "입력 안 해도 추가되는" 상태였다.
  // 폴더 필수(BRICK-31)는 2026-09-16 대교 결정으로 사라졌다 — 폴더 개념 자체가 없다.
  const widthNum = Number(width);
  const heightNum = Number(height);
  const resolutionValid =
    Number.isFinite(widthNum) && widthNum > 0 && Number.isFinite(heightNum) && heightNum > 0;
  const resolutionTouched = width.trim().length > 0 || height.trim().length > 0;
  const canSubmit = trimmed.length > 0 && !duplicate && resolutionValid && !busy;
  const pickedDevice = sizedDevices.find((device) => device.id === deviceId) ?? null;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({
        name: trimmed,
        resolutionWidth: widthNum,
        resolutionHeight: heightNum,
        ...(canChooseGroup && initialOwnerGroup !== null
          ? { ownerGroupId: ownerGroupId || (allowCommonGroup ? null : commonFallback || null) } : {}),
      });
      onClose();
    } catch (e) {
      // 서버 원문(영문) 대신 공용 한글 문구로(SCDS-97).
      setError(describeApiError(e) || '저장에 실패했습니다.');
      setBusy(false);
    }
  };
  const resInput =
    'w-full h-9 bg-bg border border-line rounded px-2 text-[14px] text-text tabular-nums';
  return (
    // 제출·취소는 고정 푸터로(2026-09-16 검수 P2) — 내용이 길어져도 버튼 자리가 그대로다.
    <ModalShell
      title={mode === 'create' ? '플레이리스트 추가' : '플레이리스트 수정'}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>취소</Button>
          <Button variant="primary" disabled={!canSubmit} onClick={() => void submit()}>
            {busy ? '저장 중…' : mode === 'create' ? '추가' : '저장'}
          </Button>
        </>
      }
    >
      <div>
        <Field
          label="플레이리스트명 *"
          value={name}
          autoFocus
          inputClassName={duplicate ? 'border-red focus:border-red' : ''}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
        />
        {duplicate && (
          <p className="mt-1 text-[12px] text-red">중복된 플레이리스트명입니다.</p>
        )}
      </div>

      {/* 조직 Owner·중앙 관리자는 어느 그룹 것으로 만들지 고른다(2026-09-17 대교). 고르지 않으면
          공통(중앙 소유)이고, 공통은 모든 그룹 기기에 편성할 수 있다. 담당 그룹이 하나뿐인
          구성원에게는 이 칸이 없다 — 고를 것이 없으므로 그 그룹으로 자동으로 정해진다.
          여러 그룹을 맡았으면 그중에서 고른다(2026-09-18 대교: "A그룹에 생성, B그룹에 생성"). */}
      {canChooseGroup && initialOwnerGroup !== null && (
        <ContentOwnerGroupField value={ownerGroupId} onChange={setOwnerGroupId}
          groups={ownableGroups ?? groups} allowCommon={allowCommonGroup} noun="플레이리스트" />
      )}

      <div>
        <span className="block font-mono text-[12px] font-medium uppercase tracking-monoWide text-text-dim mb-1.5">
          해상도 *
        </span>
        {/* 등록된 기기에서 해상도를 가져온다(2026-09-16 대교 지적 5). 고른 뒤에도 아래 폭·높이를
            그대로 고칠 수 있다 — 값을 바꾸면 이 선택은 '직접 입력'으로 돌아간다. */}
        <select
          value={deviceId}
          onChange={(e) => {
            setDeviceId(e.target.value);
            const device = sizedDevices.find((item) => item.id === e.target.value);
            // '직접 입력'으로 돌아갈 때는 지금 숫자를 그대로 두고 잠금만 푼다 — 고른 값에서
            // 조금 고치고 싶은 경우가 대부분이라 굳이 비우지 않는다.
            if (!device) return;
            setWidth(String(device.resolutionW));
            setHeight(String(device.resolutionH));
          }}
          className="mb-2 w-full h-9 bg-bg border border-line rounded px-2 text-[14px] text-text"
          aria-label="등록된 기기에서 해상도 가져오기"
          title={sizedDevices.length === 0 ? '해상도가 등록된 기기가 없습니다 — 직접 입력하세요' : '기기를 고르면 그 기기의 해상도를 그대로 씁니다'}
          disabled={sizedDevices.length === 0}
        >
          <option value="">직접 입력</option>
          {sizedDevices.map((device) => (
            <option key={device.id} value={device.id}>
              {device.name} — {device.resolutionW}×{device.resolutionH}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            placeholder="W"
            value={width}
            readOnly={!!pickedDevice}
            onChange={(e) => setWidth(e.target.value)}
            // 읽기 전용인 게 글자색만으로는 안 보였다 — 다른 비활성 입력과 같은 꼴로 눕힌다
            // (2026-09-18 대교: "선택불가능한 회색처리 필요").
            className={cn(resInput, pickedDevice && 'bg-panel-hover text-text-dim cursor-not-allowed opacity-60')}
            aria-label="해상도 가로(px)"
          />
          <span className="text-text-dim">x</span>
          <input
            type="number"
            min={1}
            placeholder="H"
            value={height}
            readOnly={!!pickedDevice}
            onChange={(e) => setHeight(e.target.value)}
            className={cn(resInput, pickedDevice && 'bg-panel-hover text-text-dim cursor-not-allowed opacity-60')}
            aria-label="해상도 세로(px)"
          />
        </div>
        {resolutionTouched && !resolutionValid && (
          <p className="mt-1 text-[12px] text-red">
            해상도는 가로·세로 모두 1 이상이어야 합니다.
          </p>
        )}
        <p className="mt-1.5 text-[12px] text-text-dim leading-[1.5]">
          {pickedDevice
            ? `ⓘ ‘${pickedDevice.name}’의 해상도를 그대로 씁니다. 직접 정하려면 위에서 ‘직접 입력’을 고르세요.`
            : 'ⓘ 플레이리스트의 기본 해상도(px)를 입력합니다.'}
          <br />
          타임라인에 추가된 플레이리스트는 이 해상도를 기준으로 노출됩니다.
        </p>
      </div>

      {error && <div className="font-mono text-[12px] text-red">{error}</div>}
    </ModalShell>
  );
}

// -----------------------------------------------------------------------------
// Playlist editor — Info + ordered Items list. Media grid moved to bottom
// section; items are queued for addition via `pendingAdd` handoff.
// -----------------------------------------------------------------------------
type DraftItem = {
  sourceKind: 'media' | 'webUrl';
  mediaId: string | null;
  noticeId: string | null;
  durationSec: number | null;
  webUrl: string | null;
  webProvider: WebVideoProvider | null;
  webMeta: WebVideoMeta | null;
  fitMode: MediaFitMode;
  noticeFitMode?: MediaFitMode | null;
};

type FitDialogState =
  | { kind: 'add'; media: MediaFitModalMedia; item: DraftItem }
  | { kind: 'edit'; media: MediaFitModalMedia; index: number; current: MediaFitMode };

const PROVIDER_BADGE: Record<WebVideoProvider, string> = {
  youtube: 'YT',
  vimeo: 'VM',
  mp4: 'MP4',
};

export function PlaylistEditor({
  playlist,
  canCreate,
  mediaList,
  notices,
  pendingAdd,
  onConsumeAdd,
  pendingRemove = null,
  onConsumeRemove,
  onSave,
  onPreview,
}: {
  playlist: Playlist;
  canCreate: boolean;
  mediaList: MediaItem[];
  notices: Notice[];
  pendingAdd: LibraryPick | null;
  onConsumeAdd: () => void;
  /** 라이브러리에서 방금 지운 콘텐츠 — 초안에서 같이 뺀다. */
  pendingRemove?: { kind: 'media' | 'notice'; id: string } | null;
  onConsumeRemove?: (() => void) | undefined;
  onPreview: () => void;
  onSave: (patch: {
    name?: string;
    imageDurationSec?: number;
    transitionType?: PlaylistTransitionType;
    transitionDurationMs?: number;
    items?: Array<{
      sourceKind?: 'media' | 'webUrl';
      mediaId?: string | null;
      noticeId?: string | null;
      webUrl?: string | null;
      webProvider?: WebVideoProvider | null;
      webMeta?: WebVideoMeta | null;
      durationSec: number | null;
      fitMode?: MediaFitMode;
      noticeFitMode?: MediaFitMode | null;
    }>;
  }) => Promise<void>;
}) {
  // 이름·이미지 기본 해상도(초)는 추가/수정 모달에서 편집 (Figma 문서 구성).
  const readOnly = playlist.canWrite === false;
  // 모바일은 손끝 끌기로 순서를 바꾸면 스크롤과 엉킨다 — 대신 ▲▼ 버튼(2026-09-23 합의).
  const isMobile = useIsMobile();
  const moveItem = (from: number, to: number) => setItems((prev) => {
    if (to < 0 || to >= prev.length) return prev;
    const next = [...prev];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);
    return next;
  });
  // 대행 모드(읽기 전용)에서도 편집 컨트롤이 활성이던 것 — 게이트를 한 곳에서(검수 S1).
  const editorWrite = useWriteGate();
  const [editingPlaylistNotice, setEditingPlaylistNotice] = useState<Notice | null>(null);
  // 정보 패널에서는 노출하지 않지만 저장 페이로드는 기존 값을 유지한다.
  const name = playlist.name;
  const imgDur = String(playlist.imageDurationSec);
  const [transitionType, setTransitionType] = useState<PlaylistTransitionType>(
    playlist.transitionType,
  );
  const [transitionMs, setTransitionMs] = useState(
    String(playlist.transitionDurationMs),
  );
  const [items, setItems] = useState<DraftItem[]>(
    playlist.items.map((i) => ({
      sourceKind: i.sourceKind ?? 'media',
      mediaId: i.mediaId,
      noticeId: i.noticeId,
      durationSec: i.durationSec ?? null,
      webUrl: i.webUrl ?? null,
      webProvider: i.webProvider ?? null,
      webMeta: i.webMeta ?? null,
      fitMode: i.fitMode ?? 'contain',
      noticeFitMode: i.noticeFitMode ?? null,
    })),
  );
  // Web URL add modal — open/close + transient validation state.
  const [webModalOpen, setWebModalOpen] = useState(false);
  // AWS에서는 YouTube 다운로드가 막혀 버튼을 숨긴다 — 서버 설정을 따른다(client/webVideoUrl).
  const webVideoUrlEnabled = useWebVideoUrlEnabled();
  // '변경 취소' 확인 팝업(공용 ConfirmModal). 마지막 저장 상태로 되돌린다.
  const [resetOpen, setResetOpen] = useState(false);
  const [fitDialog, setFitDialog] = useState<FitDialogState | null>(null);
  const [recentlyAddedIndex, setRecentlyAddedIndex] = useState<number | null>(null);

  // 편집 기준점 — 편집기를 열었을 때(또는 마지막 저장·새로고침 때)의 저장본. 변경 판정(dirty)은 이것과
  // 비교한다. 다른 PC 가 저장해 playlist 가 바뀌어도 내가 손대지 않았으면 저장 버튼이 켜지면 안 된다(SCDS-103).
  const [base, setBase] = useState<Playlist>(playlist);
  // 편집 중인 draft를 마지막 저장 상태(playlist)로 되돌린다.
  const resetDraft = (): void => {
    setBase(playlist);
    setTransitionType(playlist.transitionType);
    setTransitionMs(String(playlist.transitionDurationMs));
    setItems(
      playlist.items.map((i) => ({
        sourceKind: i.sourceKind ?? 'media',
        mediaId: i.mediaId,
        noticeId: i.noticeId,
        durationSec: i.durationSec ?? null,
        webUrl: i.webUrl ?? null,
        webProvider: i.webProvider ?? null,
        webMeta: i.webMeta ?? null,
        fitMode: i.fitMode ?? 'contain',
      noticeFitMode: i.noticeFitMode ?? null,
      })),
    );
  };

  useEffect(() => {
    if (!readOnly) return;
    resetDraft();
    setWebModalOpen(false);
    setResetOpen(false);
    setFitDialog(null);
  }, [playlist, readOnly]);

  // 저장이 끝나면 서버가 돌려준 항목으로 편집기를 맞춘다.
  //
  // 남의 그룹 자료를 담으면 서버가 이 그룹 복제본으로 항목 id 를 바꿔 저장한다. 그 결과를 받지
  // 않으면 편집기가 들고 있는 원래 id 와 저장본이 영영 달라, 저장이 됐는데도 '저장해야 미리보기에
  // 반영'과 저장 버튼이 그대로 남는다(2026-09-18 대교). 다른 화면을 다녀오면 멀쩡해 보이던 이유도
  // 이것 — 그때는 편집기가 새로 마운트돼 playlist 로 다시 초기화된다.
  const justSavedRef = useRef(false);
  // 내 저장이 만든 갱신인가 — 저장하는 동안 들어온 갱신, 또는 내가 저장한 revision 이하의 갱신.
  // 서버는 저장하자마자 playlist:change 를 보내고, 사본이 생기는 저장은 응답 뒤 사본 정보를 더 불러온다.
  // 그래서 실시간 알림이 응답보다 먼저 도착하면 내 저장을 '다른 관리자가 먼저 저장'으로 오해했다(2026-10-14 대교).
  const savingRef = useRef(false);
  const ownRevisionRef = useRef<number | null>(null);
  const ownChange = (next: Playlist): boolean => savingRef.current
    || (ownRevisionRef.current !== null && typeof next.revision === 'number' && next.revision <= ownRevisionRef.current);
  // 다른 PC 가 먼저 저장했는가 — 저장본이 내 기준점과 다르다(SCDS-103, 2026-10-01 대교 2안).
  const remoteChanged = playlist !== base && !ownChange(playlist) && (playlist.revision !== base.revision || playlist.updatedAt !== base.updatedAt);
  useEffect(() => {
    if (playlist === base) return;
    if (ownChange(playlist)) { justSavedRef.current = false; resetDraft(); return; }
    if (justSavedRef.current) { justSavedRef.current = false; resetDraft(); return; }
    // 손댄 것이 없으면 새 저장본을 그대로 따른다 — 목록이 갱신되고 저장 버튼은 꺼진 채다.
    // 손댄 것이 있으면 draft 를 지키고 '새로고침' 안내를 띄운다(아래 remoteChanged). 저장은 그동안 막는다 —
    // 서버의 revision 검사는 store 가 이미 새 revision 을 알고 있어 조용히 덮어쓰게 된다.
    if (!dirtyDraft()) resetDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist]);

  // Duration clamp:
  //  - `none` ignores the value entirely → store 0 so the Player short-circuits
  //    to hard-cut.
  //  - Any fade type with a sub-200ms duration looks like a cut anyway (the
  //    incoming-element decode wait alone eats 50-300ms), so floor the value
  //    to 200ms instead of trusting an accidental "1" the operator typed.
  //  - 상한은 화면 안내(200~9999ms)와 같은 공용 상수다. 예전엔 여기만 3000이 박혀 있어
  //    5000을 넣으면 화면엔 5000, 저장은 3000이 됐다(SCDS-90).
  const rawMs = Number(transitionMs);
  const clampedMs =
    transitionType === 'none'
      ? 0
      : Math.max(TRANSITION_MIN_MS, Math.min(TRANSITION_MAX_MS, Math.round(Number.isFinite(rawMs) ? rawMs : TRANSITION_DEFAULT_MS)));
  // 변경 판정은 base(내가 본 저장본) 기준이다. playlist 와 비교하면 남의 저장만으로 '변경됨'이 된다(SCDS-103).
  const dirtyDraft = (): boolean =>
    transitionType !== base.transitionType ||
    clampedMs !== base.transitionDurationMs ||
    !itemsEqual(items, base.items);
  const dirty = dirtyDraft();

  // ---------------------------------------------------------------------------
  // 미리보기가 "실제로" 재생하는 것 (2026-09-17 대교 지적).
  //
  // onPreview는 `playlistPreviewUrl(playlist.id)`로 플레이어 창을 연다(Playlists.tsx
  // 상단 `<PlaylistEditor onPreview=...>`). 그 URL은 `?preview=playlist&playlistId=<id>`
  // 뿐이고, 플레이어는 이 id로 서버에서 플레이리스트를 다시 받아 재생한다
  // (packages/player/src/client/previewMode.ts — startPlaylistPreview: playbackSource를
  // { kind:'playlist', playlistId }로 세팅하고 GET /api/playlists/:id 로 해상도를 읽는다).
  // 즉 편집 중 draft(items/전환 설정)는 미리보기 창으로 전혀 전달되지 않는다.
  //
  // 그래서 두 가지가 따라온다.
  //  · 판정 기준은 draft가 아니라 저장본(playlist.items)이다. 예전 BRICK-49는 "draft를
  //    비우면 비활성"으로 바꿨지만, 그건 미리보기가 draft를 재생한다는 전제였다. 저장본을
  //    재생하는 이상 draft를 비웠어도 저장본 재생은 여전히 유효한 동작이므로 막지 않는다.
  //  · 저장 안 된 변경이 있으면 "지금 보는 화면과 다른 것이 나온다"는 사실을 알려야 한다.
  //    다만 비활성으로 만들지는 않는다(저장본 미리보기 자체는 언제나 유효하다).
  // ---------------------------------------------------------------------------
  const savedItemCount = playlist.items.length;
  const previewDisabled = savedItemCount === 0;
  const previewHint = previewDisabled
    ? '저장된 항목이 없습니다. 항목을 추가하고 저장하면 미리보기할 수 있습니다.'
    : dirty
      ? '저장된 내용을 재생합니다. 지금 편집 중인 변경은 저장해야 반영됩니다.'
      : '저장된 내용을 새 창에서 재생합니다.';

  // Save status machine. Without this the Save button gives the operator
  // zero feedback — it just stays clickable, which feels broken even when
  // the request succeeded. Drives the button label and a transient inline
  // confirmation/error message.
  const [saveState, setSaveState] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  // Auto-dismiss the "Saved" pill 2s after success so the button returns
  // to its neutral state for the next edit.
  useEffect(() => {
    if (saveState !== 'saved') return;
    const t = window.setTimeout(() => setSaveState('idle'), 2000);
    return () => window.clearTimeout(t);
  }, [saveState]);
  // Any new edit after a save resets the badge so it doesn't linger over
  // a now-stale set of values.
  useEffect(() => {
    if (dirty && saveState !== 'idle' && saveState !== 'saving') {
      setSaveState('idle');
      setSaveError(null);
    }
  }, [dirty, saveState]);

  const mediaById = useMemo(() => {
    const m = new Map<string, MediaItem>();
    mediaList.forEach((mi) => m.set(mi.id, mi));
    return m;
  }, [mediaList]);

  const mediaPreviewUrl = (media: MediaItem): string =>
    media.thumbnailRelPath ? api.mediaThumbnailUrl(media.id) : api.mediaFileUrl(media.id);

  const appendDraftItem = (item: DraftItem, label: string): void => {
    setRecentlyAddedIndex(items.length);
    setItems((prev) => [...prev, item]);
    // 같은 미디어를 두 번 넣는 건 흔한 편성이지 실수가 아니다 — 따로 경고하지 않는다
    // (2026-09-17 대교). 목록에 줄이 하나 더 생긴 것으로 이미 보인다.
    toast(`'${label}'을 플레이리스트에 추가했습니다 · 저장 전`, 'success');
  };

  const requestMediaAppend = (media: MediaItem, durationSec: number | null): void => {
    if (readOnly) return;
    const item: DraftItem = {
      sourceKind: 'media', mediaId: media.id, noticeId: null, durationSec,
      webUrl: null, webProvider: null, webMeta: null, fitMode: 'contain',
    };
    if (!MEDIA_FIT_CONTROLS_VISIBLE) {
      appendDraftItem(item, media.originalName);
      return;
    }
    const modalMedia: MediaFitModalMedia = {
      name: media.originalName, width: media.width ?? 0, height: media.height ?? 0,
      previewUrl: mediaPreviewUrl(media),
    };
    if (hasResolutionMismatch(modalMedia, playlist.resolutionWidth, playlist.resolutionHeight)) {
      setFitDialog({ kind: 'add', media: modalMedia, item });
    } else {
      appendDraftItem(item, media.originalName);
    }
  };

  // 지운 콘텐츠를 초안에서도 뺀다. '없는 것을 가리키는 줄'을 남겨 두면 저장할 때까지 (missing)
  // 으로 버티다가 그대로 저장돼 버린다 — 지운 순간 목록에서 빠지는 편이 정직하다(2026-09-17 대교).
  // 목록에 없다고 지레 빼지는 않는다. 다른 그룹 소유라 안 보이는 것일 수도 있어서, 방금 지웠다는
  // 신호가 왔을 때만 뺀다.
  useEffect(() => {
    if (!pendingRemove) return;
    onConsumeRemove?.();
    const { kind, id } = pendingRemove;
    setItems((prev) => {
      const next = prev.filter((item) => (kind === 'media' ? item.mediaId : item.noticeId) !== id);
      if (next.length !== prev.length) {
        toast(`지운 ${kind === 'media' ? '미디어' : '슬라이드'}를 편집 중인 목록에서도 뺐습니다 · 저장 전`, 'info');
      }
      return next.length === prev.length ? prev : next;
    });
  }, [pendingRemove, onConsumeRemove]);

  // Consume once before opening the dialog, preventing StrictMode/repeated-click duplicates.
  useEffect(() => {
    if (!pendingAdd) return;
    onConsumeAdd();
    if (readOnly) return;
    if (fitDialog) {
      toast('먼저 맞춤 설정을 마무리해 주세요.', 'info');
      return;
    }
    if (pendingAdd.kind === 'media') {
      const isImage = pendingAdd.item.mediaType === 'image';
      requestMediaAppend(pendingAdd.item, isImage ? DEFAULT_IMAGE_DURATION : null);
    } else {
      appendDraftItem({
        sourceKind: 'media',
        mediaId: null,
        noticeId: pendingAdd.item.id,
        durationSec: DEFAULT_IMAGE_DURATION,
        webUrl: null,
        webProvider: null,
        webMeta: null,
        fitMode: 'contain',
        noticeFitMode: null,
      }, pendingAdd.item.name);
    }
  }, [pendingAdd, onConsumeAdd, fitDialog, readOnly]);

  const save = async () => {
    if (readOnly || saveState === 'saving') return;
    if (remoteChanged) {
      setSaveState('error');
      setSaveError(REMOTE_CHANGED_SAVE_REASON);
      return;
    }
    setSaveState('saving');
    setSaveError(null);
    savingRef.current = true;
    try {
      await onSave({
        name: name.trim() || playlist.name,
        imageDurationSec: Number(imgDur) || playlist.imageDurationSec,
        transitionType,
        transitionDurationMs: clampedMs,
        items,
      });
      justSavedRef.current = true;
      // 방금 저장한 revision — 이 값 이하의 갱신(늦게 온 실시간 알림 등)은 내 저장이다.
      const saved = useLibraryStore.getState().playlists.find((p) => p.id === playlist.id);
      if (typeof saved?.revision === 'number') ownRevisionRef.current = Math.max(ownRevisionRef.current ?? 0, saved.revision);
      setSaveState('saved');
    } catch (e) {
      setSaveState('error');
      setSaveError((e as Error).message || 'Unknown error');
    } finally {
      savingRef.current = false;
    }
  };

  // 총 재생시간 — 명시된 durationSec > 미디어 실측 > 웹 메타 > 이미지 기본값 순.
  const totalSec = items.reduce((sum, it) => {
    if (it.durationSec != null) return sum + it.durationSec;
    const m = it.mediaId ? mediaById.get(it.mediaId) : undefined;
    if (m?.durationSec != null) return sum + m.durationSec;
    if (it.webMeta?.durationSec != null) return sum + it.webMeta.durationSec;
    const isImage = it.sourceKind !== 'webUrl' && (m?.mediaType === 'image' || Boolean(it.noticeId));
    return sum + (isImage ? Number(imgDur) || playlist.imageDurationSec : 0);
  }, 0);

  // Pointer-event-based drag reorder.
  // The native HTML5 drag-and-drop API was unreliable here — under Electron
  // sandbox + a scrollable parent, dragstart sometimes never fires from the
  // ⋮⋮ handle and the drop event is silently swallowed. use-gesture lets us
  // own the lifecycle: track pointer Y, compute the hovered row index against
  // a refs array of row bounding rects, and reorder on release.
  const [dragFromIdx, setDragFromIdx] = useState<number | null>(null);
  // Insertion slot (0..items.length) the dragged row will land in.
  const [dropIdx, setDropIdx] = useState<number | null>(null);
  // Vertical px the dragged row is translated by so it tracks the cursor.
  const [dragDy, setDragDy] = useState(0);
  // Height (incl. the 4px flex gap) of one row — the shift unit for siblings.
  const [rowH, setRowH] = useState(0);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    if (recentlyAddedIndex === null) return;
    const frame = window.requestAnimationFrame(() => {
      rowRefs.current[recentlyAddedIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
    const timer = window.setTimeout(() => setRecentlyAddedIndex(null), 1800);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [recentlyAddedIndex]);
  // Row top/height snapshot captured at drag start. Hit-testing uses this
  // (not live rects) so sibling rows animating under the cursor don't move
  // the hit targets and make the drop slot oscillate.
  const dragRectsRef = useRef<Array<{ top: number; height: number } | null>>([]);

  /** Insertion index in [0, items.length] for a pointer clientY: the slot the
   *  dragged row would land in. Uses the start-of-drag snapshot so it stays
   *  stable while siblings animate. Returns the first row whose vertical
   *  midpoint is below the pointer, or items.length when past the last row. */
  const insertionIndexFromClientY = (clientY: number): number => {
    const rects = dragRectsRef.current;
    for (let i = 0; i < rects.length; i++) {
      const r = rects[i];
      if (r && clientY < r.top + r.height / 2) return i;
    }
    return rects.length;
  };

  // Original index of the row being dragged (fixed for the gesture's life).
  const dragFromRef = useRef<number | null>(null);
  // 방금 끝난 포인터 동작이 '손잡이에서 시작한 진짜 재정렬'이었는가 — 아래 onClickCapture 참고.
  const reorderGestureRef = useRef(false);

  // The dragged row stays in its slot and is translated to follow the cursor;
  // the items array is reordered once, on release. Keeping the array stable
  // during the drag means the row DOM is never remounted, so the pointer
  // capture taken on pointerdown survives — `last` (pointerup/cancel) always
  // fires, even when the pointer is released outside the list. We gate gesture
  // start on the pointerdown target being a [data-drag-handle] descendant so
  // clicking inputs/buttons inside a row doesn't initiate a reorder.
  const listRef = useRef<HTMLDivElement | null>(null);

  const bindList = useDrag(
    ({ event, first, last, movement: [, my], xy, tap, cancel }) => {
      if (readOnly) { cancel(); return; }
      if (tap) return;
      if (first) {
        const target = event.target as HTMLElement | null;
        const handle = target?.closest('[data-drag-handle="true"]');
        if (!handle) {
          // pointerdown landed on something else (input, button, label);
          // don't start a reorder.
          reorderGestureRef.current = false;
          cancel();
          return;
        }
        reorderGestureRef.current = true;
        const rowEl = handle.closest('[data-row-idx]') as HTMLElement | null;
        const startIdx = rowEl ? Number(rowEl.dataset.rowIdx) : -1;
        if (!Number.isFinite(startIdx) || startIdx < 0 || startIdx >= items.length) {
          cancel();
          return;
        }
        dragFromRef.current = startIdx;
        dragRectsRef.current = rowRefs.current.map((el) =>
          el
            ? { top: el.getBoundingClientRect().top, height: el.offsetHeight }
            : null,
        );
        setDragFromIdx(startIdx);
        setDropIdx(startIdx);
        setDragDy(0);
        setRowH((rowEl?.offsetHeight ?? 0) + 4);
      }
      const from = dragFromRef.current;
      if (from === null) return;

      if (last) {
        // Reorder once, on release — using the final pointer position so a
        // drop outside the list still lands at the nearest slot.
        const drop = insertionIndexFromClientY(xy[1]);
        setItems((prev) => {
          if (from < 0 || from >= prev.length) return prev;
          const insertAt = drop > from ? drop - 1 : drop;
          const clamped = Math.max(0, Math.min(insertAt, prev.length - 1));
          if (clamped === from) return prev;
          const next = [...prev];
          const [moved] = next.splice(from, 1);
          next.splice(clamped, 0, moved!);
          return next;
        });
        dragFromRef.current = null;
        setDragFromIdx(null);
        setDropIdx(null);
        setDragDy(0);
        return;
      }

      // Live: translate the dragged row to follow the cursor + track the slot
      // it would drop into (for the insertion indicator).
      setDragDy(my);
      setDropIdx(insertionIndexFromClientY(xy[1]));
    },
    { filterTaps: true, threshold: 4, pointer: { capture: true } },
  );

  const updateDuration = (idx: number, raw: string) => {
    if (readOnly) return;
    const n = Number(raw);
    setItems(
      items.map((it, i) =>
        i === idx
          ? { ...it, durationSec: Number.isFinite(n) && n > 0 ? n : null }
          : it,
      ),
    );
  };

  return (
    <div className="flex min-h-full flex-col gap-3">
      {/* 이름·해상도는 위 머리에 있으니 여기는 재생 설정만(2026-09-15). */}
      <Panel
        title="재생 설정"
        // 조작부가 제목 옆 남은 폭을 다 쓰고, 버튼 셋(최소 328px)이 모자라면 제목 아래 한 줄로 내려간다.
        // 모바일은 언제나 제목 아래 한 줄 전체 — 제목 옆에 끼면 버튼이 글자보다 좁아진다.
        trailingClassName="flex-1 max-md:basis-full"
        trailing={
          // 편집기 전체에 걸리는 조작은 여기 한 줄에 모은다 — 순서는 변경 취소 · 저장 ·
          // 미리보기(2026-09-17 대교). 미리보기는 전에 아래 항목 패널에 따로 떨어져
          // 있었는데, 저장과 짝이 되는 조작이라 같은 자리에 둔다.
          // 좁은 편집 칸에서 미리보기만 다음 줄로 떨어졌다(2026-09-23) — 버튼 셋은 언제나 한 줄.
          // 모바일은 셋이 폭을 똑같이 나누고, 상태 슬롯은 그 아래 줄로 내려간다.
          <div className="flex items-center justify-end gap-2 max-md:flex-wrap max-md:gap-y-0">
            {/* 상태 슬롯 — 저장됨/저장 실패/미저장 힌트가 번갈아 들어와도 오른쪽 버튼 셋이
                밀리지 않도록 버튼 왼쪽 남은 폭을 통째로 잡는다("조작부는 움직이지 않는다").
                고정 216px이던 때는 좁은 편집 칸에서 버튼을 다음 줄로 밀었다. 길면 말줄임. */}
            <div className="flex min-w-0 flex-1 items-center justify-end max-md:order-last max-md:basis-full max-md:[&:not(:empty)]:mt-2" aria-live="polite">
              {saveState === 'saved' ? (
                <span className="font-mono text-[12px] uppercase tracking-monoWide px-2 py-1 rounded-sm bg-[rgba(16,185,129,0.12)] text-green-400 border border-green-400/30">
                  ✓ 저장됨
                </span>
              ) : saveState === 'error' ? (
                <span
                  className="font-mono text-[12px] uppercase tracking-monoWide px-2 py-1 rounded-sm bg-red/10 text-red border border-red/30 max-w-full truncate"
                  title={saveError ?? undefined}
                >
                  ⚠ {saveError ?? '저장 실패'}
                </span>
              ) : dirty && !previewDisabled ? (
                // 저장 안 된 변경이 있을 때만 경고처럼 보인다 — 미리보기는 저장본을
                // 재생하니, 지금 화면과 다른 것이 나온다는 사실을 미리 알린다.
                <span
                  className="font-mono text-[12px] uppercase tracking-monoWide px-2 py-1 rounded-sm bg-warning/10 text-warning border border-warning/40 max-w-full truncate"
                  title={previewHint}
                >
                  ⚠ 저장해야 미리보기에 반영
                </span>
              ) : null}
            </div>
            {/* 라벨이 저장 → 저장 중… → 재시도로 바뀌어도 폭이 흔들리지 않게 셋 다 같은
                최소 폭으로 고정한다(가장 긴 '저장 중…'이 들어가는 104px). */}
            {remoteChanged && dirty && (
              // 남이 먼저 저장했고 나도 고치는 중 — 조용히 덮어쓰지도, 내 편집을 버리지도 않는다. 사람이 고른다(SCDS-103).
              <span role="alert" className="flex flex-wrap items-center gap-2 text-[13px] text-warning">
                다른 관리자가 먼저 저장했습니다. 새로고침하면 지금 편집한 내용은 사라집니다.
                <Button className="!py-0.5 !text-[13px]" onClick={() => { resetDraft(); setSaveState('idle'); setSaveError(null); }}>새로고침</Button>
              </span>
            )}
            <Button
              className="min-w-[104px] flex-none justify-center whitespace-nowrap max-md:min-w-0 max-md:flex-1 max-md:px-1.5 max-md:text-[15px]"
              disabled={readOnly || !dirty}
              onClick={() => setResetOpen(true)}
            >
              {/* '초기화'는 내용을 싹 비운다는 말로 읽힌다. 실제로 하는 일은 저장하지 않은 편집만
                  물리는 것이라 '변경 취소'로 적는다(2026-09-17 대교). */}
              변경 취소
            </Button>
            <Button
              variant="primary"
              className="min-w-[104px] flex-none justify-center whitespace-nowrap max-md:min-w-0 max-md:flex-1 max-md:px-1.5 max-md:text-[15px]"
              // 남이 먼저 저장했으면 눌러 봐야 실패라 아예 비활성 + 이유(SCDS-103 후속, 2026-10-01 대교). 새로고침이 길이다.
              disabled={readOnly || remoteChanged || (!dirty && saveState !== 'error') || saveState === 'saving'}
              {...(remoteChanged ? { title: REMOTE_CHANGED_SAVE_REASON } : {})}
              onClick={save}
            >
              {saveState === 'saving'
                ? '저장 중…'
                : saveState === 'error'
                  ? '재시도'
                  : '저장'}
            </Button>
            {/* 미리보기는 읽기 전용(대행·읽기 전용 플레이리스트)에서도 유효한 조작이라
                쓰기 게이트를 태우지 않는다. 비활성은 "재생할 저장본이 없을 때"뿐이고,
                이유는 언제나 title로 말한다. */}
            <Button
              className="min-w-[104px] flex-none justify-center whitespace-nowrap max-md:min-w-0 max-md:flex-1 max-md:px-1.5 max-md:text-[15px]"
              disabled={previewDisabled}
              onClick={onPreview}
              title={previewHint}
            >
              미리보기
            </Button>
          </div>
        }
      >
        <fieldset disabled={readOnly} className="flex min-w-0 flex-wrap items-end gap-6">
          <div className="flex flex-col">
            <span className="flex items-center gap-1 font-mono text-[12px] font-medium uppercase tracking-monoWide text-text-dim mb-1.5">
              전환 방법
              <InfoTooltip text="플레이리스트 내 스케줄을 전환하는 효과를 설정합니다." />
            </span>
            <Segmented
              value={transitionType}
              onChange={(v) => {
                if (readOnly) return;
                setTransitionType(v);
                // Cut 은 전환 시간을 쓰지 않아 0으로 저장된다. 그대로 Fade/Black 으로 넘어가면 0이
                // 남아 화면에는 0(허용 범위 밖)이 보이고, 저장할 때만 조용히 하한 200ms 가 된다 —
                // 눈에 띄지 않는 전환이라 "골랐는데 적용이 안 된다"가 됐다(2026-09-17 대교).
                // 전환을 켜는 순간 쓸 만한 기본값으로 올린다.
                if (v !== 'none') {
                  setTransitionMs((ms) => {
                    const n = Number(ms);
                    return Number.isFinite(n) && n >= TRANSITION_MIN_MS ? ms : String(TRANSITION_DEFAULT_MS);
                  });
                }
              }}
              className="h-9 items-center"
              options={[
                { value: 'none', label: 'Cut' },
                { value: 'crossfade', label: 'Fade' },
                { value: 'fade-to-black', label: 'Black' },
              ]}
            />
          </div>
          <div className="flex items-end gap-2">
            <Field
              label="전환 시간 (ms)"
              type="number"
              min={TRANSITION_MIN_MS}
              max={TRANSITION_MAX_MS}
              step={50}
              value={transitionMs}
              disabled={readOnly || transitionType === 'none'}
              onChange={(e) => setTransitionMs(e.target.value)}
              onBlur={(e) => {
                // 정책 강제 — 입력을 벗어나면 [TRANSITION_MIN_MS, TRANSITION_MAX_MS]로 보정해 저장값과 표시를 일치.
                if (transitionType === 'none') return;
                const n = Number(e.target.value);
                const clamped = Number.isFinite(n)
                  ? Math.max(TRANSITION_MIN_MS, Math.min(TRANSITION_MAX_MS, Math.round(n)))
                  : TRANSITION_MIN_MS;
                setTransitionMs(String(clamped));
              }}
              className="w-[160px]"
              inputClassName="h-9 py-0 !text-[14px]"
            />
            <span className="pb-2 font-mono text-[12px] text-text-dim whitespace-nowrap" title="입력 가능한 범위">
              {transitionType === 'none'
                ? 'Cut은 즉시 전환'
                : `${TRANSITION_MIN_MS}~${TRANSITION_MAX_MS}ms`}
            </span>
          </div>
        </fieldset>
      </Panel>

      <Panel
        title={`총 ${formatKoDuration(totalSec)}`}
        trailing={
          <div className="flex items-center gap-2">
            {/* 미리보기는 변경 취소·저장과 함께 위 '재생 설정' 머리로 옮겼다(2026-09-17 대교).
                여기 남는 건 항목 추가뿐이다. */}
            {webVideoUrlEnabled && <Button
              variant="ghost"
              {...editorWrite.and(!readOnly && canCreate, '이 플레이리스트에 항목을 추가할 권한이 없습니다').props}
              title={editorWrite.reason ?? 'YouTube 링크를 추가'}
              onClick={() => setWebModalOpen(true)}
            >
              + URL로 추가
            </Button>}
          </div>
        }
      >
        {items.length === 0 ? (
          <div className="font-mono text-[13px] text-text-dim py-4 text-center">
            {webVideoUrlEnabled
              ? '라이브러리에 등록된 미디어와 슬라이드 또는 URL로 직접 추가할 수 있습니다.'
              : '라이브러리에 등록된 미디어와 슬라이드를 추가할 수 있습니다.'}
          </div>
        ) : (
          <div
            ref={listRef}
            {...(bindList() as unknown as Record<string, unknown>)}
            // @use-gesture 는 드래그 뒤에 따라오는 클릭을 onClickCapture 에서 삼킨다(DragEngine.pointerClick).
            // 이 제스처는 목록 전체에 걸려 있어서, 줄 안의 조작부(요소 채우기 라디오 등)를 누른 클릭까지
            // 같이 삼켰다 — 한 번 눌러서는 안 바뀌고 다른 곳을 눌렀다 와야 먹히던 증상의 원인이다
            // (2026-09-17 대교). 손잡이에서 시작한 진짜 재정렬일 때만 그 억제를 살린다.
            // 새 포인터 동작이 시작되면 직전 제스처의 억제는 끝난 것이다. useDrag 는 threshold(4px)를
            // 넘겨야 핸들러를 부르므로, 움직이지 않는 그냥 클릭에서는 위 bindList 핸들러가 아예 돌지
            // 않는다 — 드래그로 재정렬한 뒤 남은 true 가 그대로 살아 다음 클릭 한 번을 삼켰다
            // (2026-09-18 대교: "드래그 후 한 번 더 클릭해야 라디오가 동작"). 손잡이가 아닌 곳을
            // 누르는 순간 억제를 푼다.
            onPointerDownCapture={(event) => {
              const target = event.target as HTMLElement | null;
              if (!target?.closest('[data-drag-handle="true"]')) reorderGestureRef.current = false;
            }}
            onClickCapture={(event) => {
              if (!reorderGestureRef.current) return;
              reorderGestureRef.current = false;
              (bindList() as unknown as { onClickCapture?: (e: unknown) => void }).onClickCapture?.(event);
            }}
            className="flex flex-col gap-1 touch-none"
          >
            {items.map((it, idx) => {
              const isWeb = it.sourceKind === 'webUrl';
              const m = it.mediaId ? mediaById.get(it.mediaId) : undefined;
              const notice = it.noticeId ? notices.find((n) => n.id === it.noticeId) : undefined;
              // Web URL items behave like videos for duration display — the
              // operator-supplied (or auto-extracted) durationSec drives the
              // hard-timeout fallback, not the image-duration default.
              const isImage = !isWeb && (m?.mediaType === 'image' || Boolean(it.noticeId));
              const label = isWeb
                ? it.webMeta?.title ?? it.webUrl ?? '(web URL)'
                : (m?.originalName ?? notice?.name ?? '(missing)');
              const kind = isWeb
                ? (it.webProvider ? PROVIDER_BADGE[it.webProvider] : 'web')
                : (m?.mediaType ?? (it.noticeId ? 'scene' : '—'));
              // 소스 해상도 — 씬(Notice)은 캔버스 규격, 미디어는 업로드 시
              // ffprobe로 추출한 width/height. 미기록(legacy) 미디어는 '—'.
              const resolution = notice
                ? `${notice.canvasWidth}×${notice.canvasHeight}`
                : m?.width && m.height
                  ? `${m.width}×${m.height}`
                  : '—';
              const thumbUrl = m
                ? m.thumbnailRelPath
                  ? api.mediaThumbnailUrl(m.id)
                  : m.mediaType === 'image'
                    ? api.mediaFileUrl(m.id)
                    : null
                : notice?.thumbnailRelPath
                  ? api.noticeThumbnailUrl(notice.id, notice.thumbnailRelPath)
                  : null;
              const canChangeFit = !it.noticeId && (Boolean(m) || isWeb);
              const isDragging = dragFromIdx === idx;
              const dragging = dragFromIdx !== null;
              // Sibling shift: rows between the dragged row's origin and the
              // hovered drop slot translate by one row height to open the gap
              // it will land in (animated via the transition below).
              let shiftY = 0;
              if (!isDragging && dragFromIdx !== null && dropIdx !== null) {
                if (dropIdx > dragFromIdx && idx > dragFromIdx && idx < dropIdx) {
                  shiftY = -rowH;
                } else if (
                  dropIdx < dragFromIdx &&
                  idx >= dropIdx &&
                  idx < dragFromIdx
                ) {
                  shiftY = rowH;
                }
              }
              return (
                <div
                  key={`${it.mediaId ?? it.noticeId ?? '?'}-${idx}`}
                  ref={(el) => {
                    rowRefs.current[idx] = el;
                  }}
                  data-row-idx={idx}
                  className={cn(
                    'relative flex items-center gap-2 px-2.5 py-2 bg-panel-2 border border-line rounded max-md:flex-wrap',
                    recentlyAddedIndex === idx && 'border-accent bg-accent-soft ring-2 ring-accent/40',
                    isDragging && 'opacity-90 shadow-lg',
                  )}
                  style={
                    isDragging
                      ? { transform: `translateY(${dragDy}px)`, zIndex: 50 }
                      : dragging
                        ? {
                            transform: `translateY(${shiftY}px)`,
                            transition: 'transform 160ms ease',
                          }
                        : undefined
                  }
                >
                  {!readOnly && isMobile && <span className="flex flex-none flex-col">
                    <button type="button" aria-label={`${label} 위로`} disabled={idx === 0}
                      onClick={() => moveItem(idx, idx - 1)} className="h-5 w-7 text-[11px] text-text-dim disabled:opacity-30">▲</button>
                    <button type="button" aria-label={`${label} 아래로`} disabled={idx === items.length - 1}
                      onClick={() => moveItem(idx, idx + 1)} className="h-5 w-7 text-[11px] text-text-dim disabled:opacity-30">▼</button>
                  </span>}
                  {!readOnly && !isMobile && <span
                    data-drag-handle="true"
                    className="flex-none text-text-dimmer select-none cursor-grab active:cursor-grabbing px-1"
                    title="드래그하여 순서 변경"
                  >
                    <GripIcon />
                  </span>}
                  <span className="font-mono text-[12px] text-text-dim w-6">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <span className="h-9 w-14 flex-none overflow-hidden rounded-sm bg-black flex items-center justify-center">
                    {thumbUrl ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={thumbUrl}
                        alt=""
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    ) : (
                      <span className="font-mono text-[12px] uppercase text-text-dim">
                        {kind}
                      </span>
                    )}
                  </span>
                  <span className="flex-1 min-w-0 truncate text-[14px] text-text">
                    {label}
                  </span>
                  {notice && <Button
                    aria-label={`${label} 디자인 편집`}
                    className="!h-8 !w-8 shrink-0 !p-1.5"
                    {...editorWrite.and(!readOnly && notice.canWrite !== false, '읽기 전용 슬라이드입니다').props}
                    title={editorWrite.and(!readOnly && notice.canWrite !== false, '읽기 전용 슬라이드입니다').props.title ?? '디자인 편집'}
                    onClick={() => setEditingPlaylistNotice(notice)}
                  ><svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m16 3 5 5-12 12-6 1 1-6Z M13 6l5 5" />
                  </svg></Button>}
                  <span className="font-mono text-[12px] text-text-dim w-[76px] text-right tabular-nums max-md:hidden">
                    {resolution}
                  </span>
                  <span className="font-mono text-[12px] text-text-dim w-[60px] text-right border-l border-line pl-2 max-md:hidden">
                    {kind}
                  </span>
                  {/* 모바일 줄바꿈 — 맞춤·재생 시간·삭제는 이름 아래 둘째 줄로. */}
                  <span aria-hidden="true" className="hidden basis-full max-md:block" />
                  {/* 공지(슬라이드) 항목은 슬라이드에 저장된 맞춤을 기본 선택으로 보이고, 바꾸면 이 플레이리스트에서만 덮어쓴다(noticeFitMode).
                      '슬라이드 설정 따름' 선택지는 두지 않는다(2026-09-14 P-2 3차). */}
                  <FitModeRadio label={`${label} 화면 맞춤`}
                    disabled={readOnly || saveState === 'saving' || isWeb && it.webProvider !== 'mp4' && !it.webMeta?.localMediaId}
                    value={it.noticeId ? (it.noticeFitMode ?? notice?.fitMode ?? 'contain') : it.fitMode}
                    onChange={(value) => { setItems(prev => prev.map((item, index) => index === idx
                      ? it.noticeId ? { ...item, noticeFitMode: value } : { ...item, fitMode: value } : item)); }} />
                  {isImage ? (
                    <label className="flex items-center gap-1 font-mono text-[12px] text-text-dim">
                      <input
                        type="number"
                        disabled={readOnly}
                        min={1}
                        value={it.durationSec ?? ''}
                        onChange={(e) => updateDuration(idx, e.target.value)}
                        placeholder={String(playlist.imageDurationSec)}
                        className="w-16 bg-panel border border-line rounded-sm px-1.5 py-0.5 text-[13px] text-text tabular-nums text-right"
                      />
                      <span>초</span>
                    </label>
                  ) : isWeb ? (
                    // Web items: editable hard-timeout. Defaults to the
                    // auto-extracted duration (webMeta.durationSec) when the
                    // operator hasn't overridden it. Tooltip explains the
                    // fallback semantics so operators don't think this is the
                    // playback length.
                    <label
                      className="flex items-center gap-1 font-mono text-[12px] text-text-dim"
                      title="Hard-timeout fallback when the web player's ENDED event doesn't fire (auto-extracted when blank)"
                    >
                      <input
                        type="number"
                        disabled={readOnly}
                        min={1}
                        value={it.durationSec ?? ''}
                        onChange={(e) => updateDuration(idx, e.target.value)}
                        placeholder={
                          it.webMeta?.durationSec
                            ? String(Math.round(it.webMeta.durationSec))
                            : 'auto'
                        }
                        className="w-16 bg-panel border border-line rounded-sm px-1.5 py-0.5 text-[13px] text-text tabular-nums text-right"
                      />
                      <span>초</span>
                    </label>
                  ) : (
                    <span className="flex items-center gap-1 font-mono text-[12px] text-text-dim">
                      <span className="w-16 text-right tabular-nums">
                        {m?.durationSec ? Math.round(m.durationSec) : '—'}
                      </span>
                      <span>초</span>
                    </span>
                  )}
                  {!readOnly && <button
                    type="button"
                    onClick={() => setItems(items.filter((_, i) => i !== idx))}
                    className="w-6 h-6 text-text-dim hover:text-red max-md:ml-auto max-md:h-9 max-md:w-9"
                    title="Remove"
                  >
                    ✕
                  </button>}
                </div>
              );
            })}
          </div>
        )}
      </Panel>
      {editingPlaylistNotice && <NoticeEditorModal notice={editingPlaylistNotice} mediaList={mediaList}
        onClose={() => setEditingPlaylistNotice(null)} />}
      {!readOnly && canCreate && webModalOpen && (
        <WebUrlAddModal
          onClose={() => setWebModalOpen(false)}
          onAdd={(item) => {
            // Server has already downloaded the URL and registered a
            // MediaItem. Append it as a regular media-kind playlist row so
            // the player applies the full warp pipeline immediately.
            requestMediaAppend(item, null);
          }}
        />
      )}
      {!readOnly && MEDIA_FIT_CONTROLS_VISIBLE && fitDialog && (
        <MediaFitConfirmModal
          media={fitDialog.media}
          targetWidth={playlist.resolutionWidth}
          targetHeight={playlist.resolutionHeight}
          defaultFitMode={fitDialog.kind === 'add' ? 'contain' : fitDialog.current}
          confirmLabel={fitDialog.kind === 'add' ? '추가' : '변경'}
          onCancel={() => setFitDialog(null)}
          onConfirm={(fitMode) => {
            if (fitDialog.kind === 'add') {
              appendDraftItem({ ...fitDialog.item, fitMode }, fitDialog.media.name);
            } else {
              setItems((prev) => prev.map((item, index) => index === fitDialog.index ? { ...item, fitMode } : item));
            }
            setFitDialog(null);
          }}
        />
      )}
      {!readOnly && resetOpen && (
        <ConfirmModal
          title="알림"
          message={'저장하지 않은 변경을 버리고 마지막 저장 상태로 되돌아갑니다.\n변경을 취소하시겠습니까?'}
          confirmLabel="확인"
          danger={false}
          onCancel={() => setResetOpen(false)}
          onConfirm={() => {
            resetDraft();
            toast('변경을 취소했습니다.', 'success');
          }}
        />
      )}
    </div>
  );
}

function itemsEqual(draft: DraftItem[], saved: PlaylistItem[]): boolean {
  if (draft.length !== saved.length) return false;
  for (let i = 0; i < draft.length; i++) {
    const a = draft[i]!;
    const b = saved[i]!;
    const bSourceKind = b.sourceKind ?? 'media';
    if (a.sourceKind !== bSourceKind) return false;
    if ((a.mediaId ?? null) !== (b.mediaId ?? null)) return false;
    if ((a.noticeId ?? null) !== (b.noticeId ?? null)) return false;
    if ((a.durationSec ?? null) !== (b.durationSec ?? null)) return false;
    if (a.fitMode !== (b.fitMode ?? 'contain') || (a.noticeFitMode ?? null) !== (b.noticeFitMode ?? null)) return false;
    if ((a.webUrl ?? null) !== (b.webUrl ?? null)) return false;
    if ((a.webProvider ?? null) !== (b.webProvider ?? null)) return false;
    // webMeta is operator-cached metadata; compare via shallow JSON so a
    // re-validation that returns identical fields doesn't dirty the editor.
    const aMeta = a.webMeta ? JSON.stringify(a.webMeta) : '';
    const bMeta = b.webMeta ? JSON.stringify(b.webMeta) : '';
    if (aMeta !== bMeta) return false;
  }
  return true;
}

// -----------------------------------------------------------------------------
// MediaLibraryPanel — bottom section. Shows all uploaded media with upload
// button + drag-drop zone. Clicking a tile while a playlist is selected adds
// to that playlist; otherwise plays the media immediately.
// -----------------------------------------------------------------------------
export function MediaLibraryPanel({
  mediaList,
  notices: allNotices,
  noticesError = null,
  selectedPlaylistId,
  selectedPlaylistReadOnly,
  selectedPlaylistGroupId = null,
  canCreate,
  legacyPlayback,
  onAddToPlaylist,
  onContentDeleted,
  onDeleteNotice,
  onBulkDeleteNotices,
  scopeGroups = [],
  scopeCentral = false,
  scopeActor,
  isOwner = false,
  createGroups = [],
  canChooseCreateGroup = false,
  canChooseCommonGroup = false,
}: {
  mediaList: MediaItem[];
  notices: Notice[];
  noticesError?: string | null;
  selectedPlaylistId: string | null;
  selectedPlaylistReadOnly: boolean;
  /** 고른 플레이리스트의 소유 그룹 — 담을 수 있는 자료를 이걸로 가른다(2026-09-18 대교). */
  selectedPlaylistGroupId?: string | null;
  canCreate: boolean;
  legacyPlayback: boolean;
  onAddToPlaylist: (pick: LibraryPick) => void;
  /** 콘텐츠를 지웠다 — 편집 중인 초안에서도 빼도록 위로 알린다. */
  onContentDeleted?: ((kind: 'media' | 'notice', id: string) => void) | undefined;
  onDeleteNotice: (id: string) => Promise<void>;
  /** 만들기 게이트 판정용 현재 사용자(스코프 actor) — 넣을 자리가 없으면 '＋'를 비활성한다. */
  scopeActor?: { enforced: boolean; central: boolean; groups: Array<{ groupId: string; role: string }> } | undefined;
  /** 그룹 묶음 표시용 — 볼 수 있는 그룹 목록과 중앙 권한 여부(2026-09-16 대교). */
  scopeGroups?: ScopeGroup[];
  scopeCentral?: boolean;
  /** 미디어 수정 모달의 공개 범위를 바꿀 수 있는가 — 조직 Owner만. */
  /** 조직 Owner인가 — 기본 템플릿 지정·해제에만 쓴다. */
  isOwner?: boolean;
  /**
   * 새로 올리는 미디어·슬라이드를 어느 그룹 것으로 만들지 고르는 칸(SCDS-93). 플레이리스트 만들기와 같은
   * 규칙(ownerGroupChoices) — 중앙 관리자, 또는 관리자·편집자로 두 그룹 이상을 맡은 사람에게만 연다.
   */
  createGroups?: Array<{ id: string; name: string }>;
  canChooseCreateGroup?: boolean;
  /** '내 자료(공통)'에 올릴 수 있는가 — 중앙만. */
  canChooseCommonGroup?: boolean;
  onBulkDeleteNotices: (
    body: { ids: string[] } | { filter: 'chat' },
  ) => Promise<{ deleted: string[]; skipped: Array<{ id: string; reason: string }> }>;
}) {
  // 템플릿(preset-* · isTemplate)은 슬라이드와 별개 복사본이라 라이브러리에는 안 보인다 — 편집기 '템플릿' 갤러리에서 쓰고 지운다(2026-09-16 대교).
  // 그 위에 그룹 격리를 한 번 더 건다: 탭 숫자·목록·선택·삭제가 전부 이 배열 하나만 본다(2026-09-16 대교 지적 3).
  const notices = useMemo(
    () => visibleContent(allNotices.filter(notice => !isCatalogTemplate(notice)), scopeGroups, scopeCentral),
    [allNotices, scopeGroups, scopeCentral],
  );
  // 라이브러리도 같은 격리 — 조직 전체 수가 탭에 뜨던 문제("수는 2, 보이는 건 1")의 원인.
  const scopedMediaList = useMemo(() => visibleContent(mediaList, scopeGroups, scopeCentral), [mediaList, scopeGroups, scopeCentral]);
  const uploadMedia = useCalibrationStore((s) => s.uploadMedia);
  // 새로 올릴 그룹(SCDS-93). '' = 내 자료(공통). 고를 수 없는 값이 남아 있으면 첫 선택지로 돌린다.
  const [createGroup, setCreateGroup] = useState('');
  const createGroupDefault = canChooseCommonGroup ? '' : createGroups[0]?.id ?? '';
  const canTransferGroup = (item: { scopeClassified?: boolean }) => isOwner || (scopeCentral && item.scopeClassified === false);
  const createGroupValid = createGroup === '' ? canChooseCommonGroup : createGroups.some((g) => g.id === createGroup);
  useEffect(() => { if (canChooseCreateGroup && !createGroupValid) setCreateGroup(createGroupDefault); },
    [canChooseCreateGroup, createGroupValid, createGroupDefault]);
  // 고를 수 없는 사람은 undefined — 지금처럼 서버 기본(작업 그룹 → 담당 그룹)을 따른다.
  const createOwnerGroupId: string | null | undefined = !canChooseCreateGroup ? undefined
    : createGroupValid ? (createGroup || null) : (createGroupDefault || (canChooseCommonGroup ? null : undefined));
  const deleteMedia = useCalibrationStore((s) => s.deleteMedia);
  const playMedia = useCalibrationStore((s) => s.playMedia);
  const loadMedia = useCalibrationStore((s) => s.loadMedia);
  const createNotice = useLibraryStore((s) => s.createNotice);
  const loadNotices = useLibraryStore((s) => s.loadNotices);
  // 기본 템플릿 지정 — 조직 Owner가 자기 조직 안에서(2026-09-16 대교). 지정한 슬라이드는 편집기 '템플릿 > 조직 템플릿'에 뜬다.
  // 옛 규칙(기준 조직의 H9만, 지정 시 전 조직 복제)은 폐지 — BO 전용 템플릿 개념 없음.
  // 템플릿 저장은 콘텐츠를 만들 수 있는 사람 모두(그룹 관리자·편집자 포함, 2026-09-16 대교: "그룹의 관리자가 템플릿에 등록할 수 없는 문제").
  // 대기 화면 게시는 중앙(Owner) 권한이 필요해 따로 둔다.
  const canManageTemplates = isOwner;
  // 템플릿으로 저장 = 슬라이드의 독립 복사본(isTemplate)을 만든다(2026-09-16 대교: 원본을 지워도 템플릿은 남고, 템플릿은 갤러리에서 따로 지운다).
  const uploadNoticeThumbnail = useLibraryStore((s) => s.uploadNoticeThumbnail);
  const saveAsTemplate = async (n: Notice) => {
    try {
      const created = await createNotice({
        name: n.name,
        canvasWidth: n.canvasWidth,
        canvasHeight: n.canvasHeight,
        canvasFill: n.canvasFill,
        canvasFillOpacity: n.canvasFillOpacity,
        fitMode: n.fitMode ?? 'contain',
        durationSec: n.durationSec,
        layers: n.layers.map((l) => ({ ...l, id: crypto.randomUUID() })),
        isTemplate: true,
        // 템플릿은 원본 슬라이드의 그룹을 따른다 → 갤러리 '그룹별 템플릿' 탭에서 소유 그룹 묶음으로 보인다.
        ...(n.ownerGroupId !== undefined ? { ownerGroupId: n.ownerGroupId } : {}),
      });
      // 썸네일은 원본 것을 복사(실패해도 템플릿 저장은 유효).
      if (n.thumbnailRelPath) {
        try { const blob = await (await fetch(api.noticeThumbnailUrl(n.id, n.thumbnailRelPath), { credentials: 'include' })).blob(); await uploadNoticeThumbnail(created.id, blob); } catch { /* best-effort */ }
      }
      toast(`'${n.name}'을(를) 템플릿으로 저장했습니다. 새 슬라이드 만들기 > 템플릿에 표시됩니다.`, 'success');
    } catch (e) {
      toast(describeApiError(e), 'error');
    }
  };
  // 복제 — 레이어·캔버스를 복제해 저장 후 바로 편집기로(카드 ⋮ '복제').
  const createFromTemplate = async (tpl: Notice): Promise<void> => {
    if (!canCreateHere) return;
    try {
      const created = await createNotice({
        name: `${tpl.name} 복사`,
        canvasWidth: tpl.canvasWidth,
        canvasHeight: tpl.canvasHeight,
        canvasFill: tpl.canvasFill,
        canvasFillOpacity: tpl.canvasFillOpacity,
        fitMode: tpl.fitMode ?? 'contain',
        durationSec: tpl.durationSec,
        layers: tpl.layers.map((l) => ({ ...l, id: crypto.randomUUID() })),
      });
      setCreatingNotice(false);
      setEditingNotice(created);
      toast(`'${tpl.name}'을(를) 복제했습니다.`, 'success');
    } catch (e) {
      toast(describeApiError(e), 'error');
    }
  };

  const [activeTab, setActiveTab] = useState<'media' | 'notice'>(
    'media',
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  // BRICK-81 — 슬라이드 삭제 확인. 폴더 삭제와 같은 공용 ConfirmModal 을 쓴다.
  const [noticeDeleteTarget, setNoticeDeleteTarget] = useState<Notice | null>(null);
  // 확인 모달이 열릴 때 사용처를 미리 물어본다 — 누른 뒤에 "안 된다"가 나오지 않게(2026-09-17 대교).
  // 미디어 삭제도 같은 확인 단계를 거친다 — 파괴적 동작 일관성.
  const [mediaDeleteTarget, setMediaDeleteTarget] = useState<MediaItem | null>(null);
  const mediaUsage = useContentUsage('media', mediaDeleteTarget?.id ?? null);
  const noticeUsage = useContentUsage('notice', noticeDeleteTarget?.id ?? null);
  const [uploading, setUploading] = useState<{ name: string; fraction: number } | null>(null);
  const [dragHover, setDragHover] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  // 카드 ⋮ '대기 화면으로 게시…' — 옛 편집기 '대기 화면' 버튼의 게시 기능(2026-09-16 대교). 중앙 관리자용.
  const [idleTarget, setIdleTarget] = useState<Notice | null>(null);
  const isBoOperator = useBoOperator() === 'allowed';
  const [creatingNotice, setCreatingNotice] = useState(false);
  const [mediaFilter, setMediaFilter] = useState<MediaFilter>('all');
  const [noticeFilter, setNoticeFilter] = useState<NoticeFilter>('all');
  // 기본값은 생성 순(BRICK-60/74).
  const [mediaSort, setMediaSort] = useState<MediaSort>('oldest');
  const [mediaView, setMediaView] = useState<MediaView>('list');
  // NBL_REQ_407 — name-based search, composed with the type filter + sort.
  const [mediaSearch, setMediaSearch] = useState('');
  // Scene multi-select mode — when true, tile clicks toggle selection
  // instead of adding the notice to the active playlist.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedNoticeIds, setSelectedNoticeIds] = useState<Set<string>>(new Set());
  // 라이브러리(미디어)에도 같은 선택 모드(2026-09-16 대교 4번). 삭제는 개별 DELETE를 순서대로.
  const [selectedMediaIds, setSelectedMediaIds] = useState<Set<string>>(new Set());
  const [bulkMediaDeleteTarget, setBulkMediaDeleteTarget] = useState<string[] | null>(null);
  // 일괄 삭제도 하나씩 지울 때처럼 사용처를 먼저 본다. 예전엔 쓰이는 미디어를 그대로 지우려다
  // "0개 삭제 완료 (3개 실패)"만 남겼다(SCDS-61).
  const bulkMediaUsage = useBulkContentUsage('media', bulkMediaDeleteTarget);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDeleteTarget, setBulkDeleteTarget] = useState<string[] | null>(null);
  const [previewPick, setPreviewPick] = useState<LibraryPick | null>(null);
  // ⋮ → 수정: 파일 이름·폴더·공개 범위 모달(옛 '콘텐츠 정보' 인스펙터 대신, 2026-09-15 대교).
  const [editingMedia, setEditingMedia] = useState<MediaItem | null>(null);
  // 슬라이드 ⋮ → 수정: 슬라이드명·재생시간·폴더·공개 범위 모달. 캔버스는 '디자인 편집'(editingNotice)으로 따로.
  const [noticeInfoTarget, setNoticeInfoTarget] = useState<Notice | null>(null);

  // 탭을 바꾸면 선택 모드를 끈다(두 탭 공통).
  useEffect(() => {
    setSelectMode(false);
    setSelectedMediaIds(new Set());
    if (activeTab !== 'notice') {
      setSelectedNoticeIds(new Set());
    }
  }, [activeTab]);

  useEffect(() => {
    const allowed=new Set(notices.filter(notice=>notice.canWrite!==false).map(notice=>notice.id));
    setSelectedNoticeIds(current=>{const next=new Set([...current].filter(id=>allowed.has(id)));return next.size===current.size?current:next;});
  },[notices]);

  const toggleNoticeSelected = (id: string) => {
    const notice = notices.find((n) => n.id === id);
    if (!notice || notice.canWrite === false || isBrickBriefTemplateNotice(notice)) return;
    setSelectedNoticeIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDeleteSelected = () => {
    const ids = notices.filter((n) => selectedNoticeIds.has(n.id) && n.canWrite !== false && !isBrickBriefTemplateNotice(n)).map((n) => n.id);
    if (ids.length) setBulkDeleteTarget(ids);
  };
  const toggleMediaSelected = (id: string) => {
    const item = scopedMediaList.find((m) => m.id === id);
    if (!item || item.canWrite === false) return;
    setSelectedMediaIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  };
  const selectedCount = activeTab === 'media' ? selectedMediaIds.size : selectedNoticeIds.size;
  const exitSelectMode = () => { setSelectMode(false); setSelectedNoticeIds(new Set()); setSelectedMediaIds(new Set()); };

  // ── 도구줄 게이트(2026-09-16 검수 S12·P1) ────────────────────────────────
  // 권한이 없거나 고를 게 없으면 버튼을 숨기지 않고 같은 자리에 비활성 + title(이유)로 둔다.
  const write = useWriteGate();
  // 권한 + 넣을 자리(내 자료·담당 그룹) 둘 다 있어야 만들 수 있다(2026-09-16 대교 지적 1).
  const createGate = write
    .and(canCreate, '이 계정에는 콘텐츠 생성 권한이 없습니다.')
    .and(writableTargetCount(scopeActor) > 0, NO_WRITE_TARGET_REASON);
  // 아래 '만들기' 경로는 전부 이 하나를 본다 — 버튼만 비활성하고 드래그 업로드는 열어 두면 게이트가 새어 나간다.
  const canCreateHere = !createGate.blocked;
  // '지울 수 있는' 항목 수 — 읽기 전용만 있는 계정(Editor/뷰어)에서 ☑ 선택이 헛돌던 문제.
  const deletableCount = activeTab === 'media'
    ? scopedMediaList.filter((m) => m.canWrite !== false).length
    : notices.filter((n) => n.canWrite !== false && !isBrickBriefTemplateNotice(n)).length;
  const itemWord = activeTab === 'media' ? '미디어' : '슬라이드';
  const selectGate = write.and(
    deletableCount > 0,
    (activeTab === 'media' ? scopedMediaList.length : notices.length) === 0
      ? `${itemWord}가 없습니다`
      : `삭제할 수 있는 ${itemWord}가 없습니다 — 모두 읽기 전용입니다`,
  );

  // 썸네일 버튼의 접근 이름은 클릭이 무엇을 하는지에 달렸다(검수 C8 — 1024에서 unnamed 2건).
  const pickVerb = selectMode
    ? '선택'
    : selectedPlaylistId && !selectedPlaylistReadOnly
      ? '플레이리스트에 추가'
      : '미리보기';

  const handleFiles = async (files: FileList | null) => {
    if (!canCreateHere || !files || files.length === 0) return;
    let uploaded = 0;
    for (const file of Array.from(files)) {
      // Figma 코멘트 확정안 — 지정 확장자 외에는 업로드 불가(드래그앤드롭 우회 차단).
      if (!isAllowedMediaFile(file.name)) {
        toast('지원하지 않는 파일 형식입니다. 비디오/이미지 파일만 업로드할 수 있습니다.', 'error');
        continue;
      }
      setUploading({ name: file.name, fraction: 0 });
      try {
        await uploadMedia(file, (f) => setUploading({ name: file.name, fraction: f }),
          createOwnerGroupId !== undefined ? { ownerGroupId: createOwnerGroupId } : undefined);
        uploaded += 1;
      } catch (e) {
        // 업로드 실패는 토스트 하나로만 알린다(스토어의 mediaError 는 화면이 읽지 않는다).
        toast(describeApiError(e), 'error');
      }
    }
    setUploading(null);
    if (uploaded > 0) toast('업로드되었습니다.', 'success');
  };

  // A그룹 플레이리스트를 고르면 A그룹 자료만 담긴다 — 나머지는 숨기지 않고 비활성 + 이유.
  // 중앙은 예외다(담는 순간 그 그룹으로 복제된다). 판정은 서버와 같은 함수를 쓴다.
  const addBlockedReason = (item: { ownerGroupId?: string | null; scopeClassified?: boolean }): string | null =>
    selectedPlaylistId ? addToPlaylistBlockedReason(item, selectedPlaylistGroupId, scopeCentral) : null;

  const clickMedia = (m: MediaItem) => {
    if (selectedPlaylistReadOnly) return;
    const blocked = addBlockedReason(m);
    if (blocked) { toast(blocked, 'error'); return; }
    // Lottie는 Player 재생 파이프라인(image/video 전용) 미지원 — Scene 레이어로만 사용.
    if (m.mediaType === 'lottie') {
      toast('Lottie 파일은 플레이리스트에 직접 추가할 수 없습니다. 슬라이드(Scene)에서 레이어로 사용하세요.', 'error');
      return;
    }
    if (selectedPlaylistId) onAddToPlaylist({ kind: 'media', item: m });
    else if (!legacyPlayback || m.canWrite !== undefined) setPreviewPick({ kind: 'media', item: m });
    else void playMedia(m.id);
  };
  const clickNotice = (n: Notice) => {
    const blocked = addBlockedReason(n);
    if (blocked) { toast(blocked, 'error'); return; }
    if (selectedPlaylistId && !selectedPlaylistReadOnly) onAddToPlaylist({ kind: 'notice', item: n });
    // No immediate-play path for notices yet (Player compositor TBD).
  };

  const visibleMediaList = useMemo(() => {
    const filtered = scopedMediaList.filter(
      (media) =>
        mediaMatchesFilter(media, mediaFilter) &&
        mediaMatchesSearch(media, mediaSearch),
    );
    return filtered.slice().sort((a, b) => {
      if (mediaSort === 'name') return a.originalName.localeCompare(b.originalName, 'ko');
      if (mediaSort === 'type') {
        const byType = mediaTypeLabel(a).localeCompare(mediaTypeLabel(b), 'ko');
        return byType || a.originalName.localeCompare(b.originalName, 'ko');
      }
      if (mediaSort === 'duration') return (b.durationSec ?? -1) - (a.durationSec ?? -1);
      // 생성 순 — 오래된 항목이 위(BRICK-60).
      if (mediaSort === 'oldest') return a.createdAt.localeCompare(b.createdAt);
      return b.createdAt.localeCompare(a.createdAt);
    });
  }, [mediaFilter, scopedMediaList, mediaSort, mediaSearch]);

  // 슬라이드 탭 정렬(BRICK-74~76) — 라이브러리와 같은 3종 옵션을 공유한다.
  const sortNotices = useCallback(
    (list: Notice[]): Notice[] =>
      list.slice().sort((a, b) => {
        if (mediaSort === 'duration') return (b.durationSec ?? -1) - (a.durationSec ?? -1);
        if (mediaSort === 'oldest') return (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
        return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '');
      }),
    [mediaSort],
  );

  // 슬라이드도 검색·타입 필터를 거친 목록으로 그린다(라이브러리와 동일).
  const visibleNotices = useMemo(() => notices.filter((n) => noticeMatchesFilter(n, noticeFilter) && noticeMatchesSearch(n, mediaSearch)), [notices, noticeFilter, mediaSearch]);
  // 폴더 섹션("지정되지 않음" 포함)은 2026-09-16 대교 결정으로 전부 없앴다 — 소유 그룹 묶음 하나로만 나눈다.
  // null이면 볼 수 있는 묶음이 하나뿐이라는 뜻 → 머리 없이 평평하게 그린다.
  const mediaSections = useMemo(() => groupSections(visibleMediaList, scopeGroups, scopeCentral), [visibleMediaList, scopeGroups, scopeCentral]);
  const noticeSections = useMemo(() => groupSections(visibleNotices, scopeGroups, scopeCentral), [visibleNotices, scopeGroups, scopeCentral]);
  // 라이브러리와 슬라이드는 같은 그룹이라도 따로 접는다 — 탭마다 보고 싶은 묶음이 다르다.
  const mediaFold = useCollapsedSections('library-media');
  const noticeFold = useCollapsedSections('library-notices');

  const renderMediaGroup = (list: MediaItem[]) =>
    mediaView === 'list' ? (
      <div className="flex flex-col gap-1.5">
        {list.map((m) => (
          <MediaListRow
            key={m.id}
            item={m}
            selectMode={selectMode}
            selected={selectedMediaIds.has(m.id)}
            disabled={selectMode ? m.canWrite === false : addBlockedReason(m) !== null}
            {...(!selectMode && addBlockedReason(m) ? { disabledReason: addBlockedReason(m)! } : {})}
            pickLabel={`${m.originalName} ${pickVerb}`}
            onPick={() => selectMode ? toggleMediaSelected(m.id) : clickMedia(m)}
            onPreview={() => setPreviewPick({ kind: 'media', item: m })}
            onEdit={() => setEditingMedia(m)}
            onDelete={() => setMediaDeleteTarget(m)}
          />
        ))}
      </div>
    ) : (
      <div className="grid grid-cols-2 gap-2">
        {list.map((m) => (
          <MediaTile
            key={m.id}
            item={m}
            selectMode={selectMode}
            selected={selectedMediaIds.has(m.id)}
            disabled={selectMode ? m.canWrite === false : addBlockedReason(m) !== null}
            {...(!selectMode && addBlockedReason(m) ? { disabledReason: addBlockedReason(m)! } : {})}
            pickLabel={`${m.originalName} ${pickVerb}`}
            onPick={() => selectMode ? toggleMediaSelected(m.id) : clickMedia(m)}
            onPreview={() => setPreviewPick({ kind: 'media', item: m })}
            onEdit={() => setEditingMedia(m)}
            onDelete={() => setMediaDeleteTarget(m)}
          />
        ))}
      </div>
    );

  const renderNoticeGroup = (list: Notice[]) => (
    <div className={mediaView === 'list' ? 'flex flex-col gap-1.5' : 'grid grid-cols-2 gap-2'}>
      {sortNotices(list).map((n) => {
        const isPreset = n.id.startsWith('preset-') || isBrickBriefTemplateNotice(n);
        const isSelected = selectedNoticeIds.has(n.id);
        const Row = mediaView === 'list' ? NoticeListRow : NoticeTile;
        return (
          <Row
            key={n.id}
            notice={n}
            onPreview={() => setPreviewPick({ kind: 'notice', item: n })}
            onEditInfo={() => { if (n.canWrite !== false) setNoticeInfoTarget(n); }}
            onPick={() => {
              if (selectMode) {
                if (isPreset) return; // presets/templates can't be deleted
                toggleNoticeSelected(n.id);
              } else {
                clickNotice(n);
              }
            }}
            onEdit={() => {
              if (n.canWrite === false) return;
              setEditingNotice(n);
              setCreatingNotice(false);
            }}
            {...(canCreateHere ? { onDuplicate: () => void createFromTemplate(n) } : {})}
            onSaveTemplate={canCreateHere && n.canWrite !== false ? () => void saveAsTemplate(n) : undefined}
            onPublishIdle={canManageTemplates ? () => setIdleTarget(n) : undefined}
            onDelete={() => {
              // 기본 템플릿은 Owner(기준 조직)만 삭제 — 다른 조직 사본도 함께 제거됨(모달에서 안내).
              if (n.canWrite === false || isPreset && !canManageTemplates) return;
              setNoticeDeleteTarget(n);
            }}
            selectMode={selectMode}
            selected={isSelected}
            disabled={selectMode ? isPreset || n.canWrite === false : addBlockedReason(n) !== null}
            {...(!selectMode && addBlockedReason(n) ? { disabledReason: addBlockedReason(n)! } : {})}
            pickLabel={`${n.name} ${pickVerb}`}
          />
        );
      })}
    </div>
  );

  return (
    <div
      className={cn(
        'h-full flex flex-col bg-panel overflow-hidden',
        dragHover && activeTab === 'media' && 'ring-2 ring-accent/40',
      )}
      onDragOver={(e) => {
        if (!canCreateHere || activeTab !== 'media') return;
        e.preventDefault();
        setDragHover(true);
      }}
      onDragLeave={() => setDragHover(false)}
      onDrop={(e) => {
        if (!canCreateHere || activeTab !== 'media') return;
        e.preventDefault();
        setDragHover(false);
        void handleFiles(e.dataTransfer.files);
      }}
    >
      <header className="flex flex-col gap-3 px-3 py-3 border-b border-line bg-panel">
        <div className="flex min-w-0 items-center gap-2">
          {/* role=tablist/tab/aria-selected — 시각 디자인은 그대로 두고 접근성만 채운다(검수 C4). */}
          {/* 탭 숫자는 목록과 **같은 배열**의 길이다 — 조직 전체 수를 쓰면 A그룹에 1개만 보이는데 2로 뜬다(2026-09-16 대교 지적 3). */}
          <div role="tablist" aria-label="라이브러리 / 슬라이드" className="flex min-w-0 items-center gap-2">
            <TabBtn
              active={activeTab === 'media'}
              onClick={() => setActiveTab('media')}
              count={scopedMediaList.length}
              label="라이브러리"
            />
            <TabBtn
              active={activeTab === 'notice'}
              onClick={() => setActiveTab('notice')}
              count={notices.length}
              label="슬라이드"
            />
          </div>
          <span className="ml-auto" />
          {/* 생성 권한이 없어도 자리를 지킨다 — 비활성 + 이유(검수 S12). */}
          {/* 탭이 정하는 동작 하나뿐이라 메뉴를 거치지 않는다. '템플릿에서 만들기'는 없앴다 —
              템플릿은 새 슬라이드 편집기 안의 '템플릿' 버튼으로 언제든 열 수 있다(2026-09-17 대교). */}
          <ToolbarButton glyph="＋" className="flex-none" disabled={createGate.blocked}
            title={createGate.reason ?? (activeTab === 'media' ? '미디어 업로드하기' : '슬라이드 만들기')}
            onClick={() => {
              if (activeTab === 'media') { fileInputRef.current?.click(); return; }
              setEditingNotice(null); setCreatingNotice(true);
            }} />
          {/* 정렬은 미디어·슬라이드 두 탭 공통(BRICK-74~76). */}
          <ToolbarMenu glyph="≡" title="정렬" className="flex-none" align="right">
            {MEDIA_SORT_OPTIONS.map((o) => (
              <MenuRow
                key={o.value}
                label={o.label}
                checked={mediaSort === o.value}
                onClick={() => setMediaSort(o.value)}
              />
            ))}
          </ToolbarMenu>
        </div>
        {/* 도구줄은 두 탭 공통(2026-09-16 대교): 이름 검색 · 타입 · 격자/목록 · 선택. 타입 선택지만 탭에 따라 다르다. */}
        <div className="flex min-h-7 flex-wrap items-center gap-1.5">
          <input
            type="search"
            value={mediaSearch}
            onChange={(e) => setMediaSearch(e.target.value)}
            placeholder="이름 검색…"
            aria-label={activeTab === 'media' ? '이름으로 미디어 검색' : '이름으로 슬라이드 검색'}
            title={activeTab === 'media' ? '이름으로 미디어 검색' : '이름으로 슬라이드 검색'}
            className="h-7 min-w-[10rem] flex-[1_1_10rem] bg-bg border border-line rounded-sm px-2 font-mono text-[12px] text-text placeholder:text-text-dim"
          />
          {/* 좁아지면 검색만 첫 줄, 타입·보기·선택은 한 덩어리로 둘째 줄(2026-09-16 대교). */}
          <div className="ml-auto flex flex-none items-center gap-1.5">
          <span className="font-mono text-[12px] text-text-dim">타입</span>
          {activeTab === 'media' ? (
            <select
              value={mediaFilter}
              onChange={(e) => setMediaFilter(e.target.value as MediaFilter)}
              className="h-7 bg-bg border border-line rounded-sm px-2 font-mono text-[12px] text-text"
              title="타입 필터"
            >
              <option value="all">전체</option>
              <option value="video">비디오</option>
              <option value="image">이미지</option>
            </select>
          ) : (
            <select
              value={noticeFilter}
              onChange={(e) => setNoticeFilter(e.target.value as NoticeFilter)}
              className="h-7 bg-bg border border-line rounded-sm px-2 font-mono text-[12px] text-text"
              title="타입 필터"
            >
              <option value="all">전체</option>
              <option value="landscape">가로</option>
              <option value="portrait">세로</option>
            </select>
          )}
          <div className="flex border border-line rounded-sm overflow-hidden">
            <button
              type="button"
              onClick={() => setMediaView('grid')}
              aria-label="격자 보기"
              title="격자 보기"
              className={cn(
                'h-7 w-8 inline-flex items-center justify-center',
                mediaView === 'grid' ? 'bg-accent text-bg' : 'bg-bg text-text-dim hover:text-text',
              )}
            >
              <ColumnsIcon />
            </button>
            <button
              type="button"
              onClick={() => setMediaView('list')}
              aria-label="목록 보기"
              title="목록 보기"
              className={cn(
                'h-7 w-8 inline-flex items-center justify-center border-l border-line',
                mediaView === 'list' ? 'bg-accent text-bg' : 'bg-bg text-text-dim hover:text-text',
              )}
            >
              <ListIcon />
            </button>
          </div>
          {/* UI 원칙(2026-09-16 대교): 조작부는 사용자의 상태 선택으로 위치·크기가 바뀌지 않는다 — 리모컨 버튼이 움직이면 안 되는 것과 같다.
              그래서 '☑ 선택'은 선택 모드에서도 같은 자리에 눌린 상태(토글)로 남고, 작업 줄은 아래에 추가만 된다. */}
          <Button className={cn('!h-7 !py-0 !text-[12px]',
            selectMode && '!border-accent !bg-accent-soft !text-accent')} aria-pressed={selectMode}
            onClick={() => selectMode ? exitSelectMode() : setSelectMode(true)}
            disabled={selectGate.blocked}
            title={selectGate.reason ?? (selectMode ? '선택 모드 끄기' : '여러 개를 골라 한 번에 삭제')}>☑ 선택</Button>
          </div>
          {/* 두 그룹 이상을 맡았거나 중앙이면 ＋로 만드는 미디어·슬라이드의 그룹을 여기서 고른다(SCDS-93).
              예전엔 고를 곳이 없어 늘 첫 그룹(중앙은 내 자료)으로 들어갔고, 수정 창에서 옮기려 하면
              소유 이전 권한(조직 Owner)이 없어 거부됐다. */}
          {canChooseCreateGroup && (
            <label className="flex basis-full min-w-0 items-center gap-1.5 font-mono text-[12px] text-text-dim"
              title={`＋로 새로 만드는 ${itemWord}가 이 그룹 것이 됩니다`}>
              <span className="shrink-0">올릴 그룹</span>
              <select aria-label="새로 올릴 그룹" value={createGroupValid ? createGroup : createGroupDefault}
                onChange={(e) => setCreateGroup(e.target.value)} disabled={!canCreateHere}
                className="h-7 min-w-0 flex-1 bg-bg border border-line rounded-sm px-2 text-[12px] text-text">
                {canChooseCommonGroup && <option value="">내 자료 (공통)</option>}
                {createGroups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </label>
          )}
          {/* 선택 작업 줄 — 선택 모드가 아니어도 자리를 비워 두고 그대로 남긴다(검수 S8).
              예전에는 진입할 때 줄이 새로 생겨 아래의 묶음 머리·메뉴가 33px씩 밀렸다.
              원칙("조작부는 움직이지 않는다")대로, 보이지 않을 뿐 높이는 항상 같다. */}
          <div role="group" aria-label="선택 작업" aria-hidden={selectMode ? undefined : true}
            className={cn('flex basis-full items-center justify-end gap-1.5', !selectMode && 'invisible')}>
            <span className="mr-auto font-mono text-[12px] text-text-dim">{selectedCount}개 선택</span>
            <Button className="!h-7 !py-0 !text-[12px]" onClick={exitSelectMode} disabled={!selectMode || bulkBusy}
              tabIndex={selectMode ? undefined : -1}>취소</Button>
            <Button variant="danger" className="!h-7 !py-0 !text-[12px]" disabled={!selectMode || bulkBusy || selectedCount === 0}
              tabIndex={selectMode ? undefined : -1}
              // 비활성 이유를 title로 — `삭제 (0)`이 왜 안 눌리는지 알 수 없었다(검수 S8).
              title={bulkBusy ? '삭제 중입니다' : selectedCount === 0 ? `지울 ${itemWord}를 먼저 고르세요` : `고른 ${itemWord} ${selectedCount}개 삭제`}
              onClick={() => {
                if (activeTab === 'media') { const ids = scopedMediaList.filter((m) => selectedMediaIds.has(m.id) && m.canWrite !== false).map((m) => m.id); if (ids.length) setBulkMediaDeleteTarget(ids); }
                else handleBulkDeleteSelected();
              }}>
              {bulkBusy ? '삭제 중…' : `삭제 (${selectedCount})`}
            </Button>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          disabled={!canCreateHere}
          multiple
          accept={MEDIA_ACCEPT}
          className="hidden"
          onChange={(e) => {
            void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </header>

      {/* 업로드 실패는 아래 토스트 하나로만 알린다 — 여기 띠까지 띄우면 같은 말을 두 번 하고,
          직접 닫기 전까지 라이브러리 위에 남아 다음 작업을 가린다(2026-09-17 대교). 진행률은
          '지금 일어나는 일'이라 남는다. */}
      {activeTab === 'media' && uploading && (
        <div className="px-3 py-2 border-b border-line bg-panel-2 flex items-center justify-between">
          {uploading ? (
            <>
              <span className="font-mono text-[13px] text-accent">
                ↑ {uploading.name} — {Math.round(uploading.fraction * 100)}%
              </span>
              <div className="w-40 h-1 bg-panel-3 rounded overflow-hidden">
                <div
                  className="h-full bg-accent transition-all duration-fast"
                  style={{ width: `${Math.round(uploading.fraction * 100)}%` }}
                />
              </div>
            </>
          ) : null}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 flex flex-col gap-1">
        {activeTab === 'media' ? (
          scopedMediaList.length > 0 && visibleMediaList.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center font-mono text-[14px] text-text-dim py-12">
                조건에 맞는 미디어가 없습니다.
              </div>
            </div>
          ) : (
            <>
              {scopedMediaList.length === 0 ? (
                // 빈 상태는 세 탭(플레이리스트·라이브러리·슬라이드)이 같은 꼴 — 문구 + 다음 행동(검수 S18).
                <div className="flex flex-col items-center gap-3 py-10 text-center">
                  <p className="font-mono text-[13px] text-text-dim">추가된 라이브러리가 없습니다.</p>
                  <Button variant="primary" {...createGate.props}
                    onClick={() => fileInputRef.current?.click()}>＋ 미디어 업로드하기</Button>
                  {!createGate.blocked && <p className="text-[12px] text-text-dim">파일을 이 영역에 드래그해도 올라갑니다.</p>}
                </div>
              ) : <>
              {(() => {
                const queued = scopedMediaList.filter(m => m.processingStatus === 'pending').length;
                const running = scopedMediaList.filter(m => m.processingStatus === 'processing').length;
                const failed = scopedMediaList.filter(m => m.processingStatus === 'failed').length;
                return queued + running + failed > 0 && (
                  <div role="status" className="mb-1 rounded border border-line bg-panel-2 px-2.5 py-1.5 text-[12px] text-text-dim">
                    서버 영상 변환 · {running > 0 && <span className="text-accent">변환 중 {running} · </span>}대기 {queued}{failed > 0 && <span className="text-red"> · 실패 {failed}</span>}
                    <span className="block text-[11px]">변환이 끝난 영상부터 기기가 작은 변환본으로 바꿔 받습니다. 그 전까지는 원본을 받습니다.</span>
                  </div>
                );
              })()}
              {mediaSections ? mediaSections.map((section) => (
                <div key={section.key} data-group-section={section.key}>
                  <GroupSectionLabel label={section.label} central={section.central} empty={section.items.length === 0}
                    count={section.items.length} collapsed={mediaFold.isCollapsed(section.key)} onToggle={() => mediaFold.toggle(section.key)} />
                  {!mediaFold.isCollapsed(section.key) && renderMediaGroup(section.items)}
                </div>
              )) : renderMediaGroup(visibleMediaList)}
              </>}
            </>
          )
        ) : notices.length > 0 && visibleNotices.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-center font-mono text-[14px] text-text-dim py-12">조건에 맞는 슬라이드가 없습니다.</div>
          </div>
        ) : (
          <>
            {notices.length === 0 && !noticesError ? (
              // 라이브러리·플레이리스트 빈 상태와 같은 꼴로 맞춘다(검수 S18).
              <div className="flex flex-col items-center gap-3 py-10 text-center">
                <p className="font-mono text-[13px] text-text-dim">추가된 슬라이드가 없습니다.</p>
                <Button
                  variant="primary"
                  {...createGate.props}
                  onClick={() => {
                    setEditingNotice(null);
                            setCreatingNotice(true);
                  }}
                >
                  ＋ 슬라이드 만들기
                </Button>
              </div>
            ) : noticesError && notices.length === 0 ? null : noticeSections ? noticeSections.map((section) => (
              <div key={section.key} data-group-section={section.key}>
                <GroupSectionLabel label={section.label} central={section.central} empty={section.items.length === 0}
                  count={section.items.length} collapsed={noticeFold.isCollapsed(section.key)} onToggle={() => noticeFold.toggle(section.key)} />
                {!noticeFold.isCollapsed(section.key) && renderNoticeGroup(section.items)}
              </div>
            )) : renderNoticeGroup(visibleNotices)}
          </>
        )}
      </div>

      {(canCreateHere && creatingNotice || editingNotice && notices.find((n) => n.id === editingNotice.id)?.canWrite !== false) && (
        <NoticeEditorModal
          notice={editingNotice}
          mediaList={mediaList}
          {...(!editingNotice && createOwnerGroupId !== undefined ? { ownerGroupId: createOwnerGroupId } : {})}
          onClose={() => {
            setEditingNotice(null);
            setCreatingNotice(false);
          }}
        />
      )}
      {idleTarget && (
        <IdleContentSettings notice={notices.find((n) => n.id === idleTarget.id) ?? idleTarget} mediaList={mediaList} isBo={isBoOperator}
          canPublish portrait={idleTarget.canvasHeight > idleTarget.canvasWidth} onClose={() => setIdleTarget(null)} />
      )}
      {mediaDeleteTarget && (
        <ConfirmModal
          title="미디어 삭제"
          message={<>
            {`'${mediaDeleteTarget.originalName}' 미디어를 삭제하시겠습니까?`}
            {!!mediaUsage.usage?.length && <ContentUsageNotice usage={mediaUsage.usage} noun="미디어" />}
          </>}
          confirmLabel="삭제"
          confirmDisabled={mediaUsage.loading || !!mediaUsage.usage?.length}
          confirmDisabledReason={mediaUsage.loading ? '사용처를 확인하는 중입니다.' : '사용 중인 미디어는 지울 수 없습니다.'}
          onCancel={() => setMediaDeleteTarget(null)}
          onConfirm={async () => {
            if (scopedMediaList.find((m) => m.id === mediaDeleteTarget.id)?.canWrite === false) throw new Error('읽기 전용 미디어입니다.');
            await deleteMedia(mediaDeleteTarget.id);
            onContentDeleted?.('media', mediaDeleteTarget.id);
            setMediaDeleteTarget(null);
          }}
        />
      )}

      {noticeDeleteTarget && (
        <ConfirmModal
          title="슬라이드 삭제"
          message={<>
            {isTemplateNotice(noticeDeleteTarget)
              ? `기본 템플릿 '${noticeDeleteTarget.name}'을(를) 삭제하면 템플릿 목록에서도 사라집니다. 삭제할까요?`
              : `'${noticeDeleteTarget.name}' 슬라이드를 삭제하시겠습니까?`}
            {!!noticeUsage.usage?.length && <ContentUsageNotice usage={noticeUsage.usage} noun="슬라이드" />}
          </>}
          confirmLabel="삭제"
          confirmDisabled={noticeUsage.loading || !!noticeUsage.usage?.length}
          confirmDisabledReason={noticeUsage.loading ? '사용처를 확인하는 중입니다.' : '사용 중인 슬라이드는 지울 수 없습니다.'}
          onCancel={() => setNoticeDeleteTarget(null)}
          onConfirm={async () => {
            if (notices.find((n) => n.id === noticeDeleteTarget.id)?.canWrite === false) throw new Error('읽기 전용 슬라이드입니다.');
            await onDeleteNotice(noticeDeleteTarget.id);
            onContentDeleted?.('notice', noticeDeleteTarget.id);
          }}
        />
      )}
      {bulkMediaDeleteTarget && (() => {
        const usage = bulkMediaUsage.usage;
        const inUse = usage ? bulkMediaDeleteTarget.filter((id) => (usage.get(id) ?? []).length > 0) : [];
        const free = usage ? bulkMediaDeleteTarget.filter((id) => !(usage.get(id) ?? []).length) : [];
        const nameOf = (id: string) => scopedMediaList.find((m) => m.id === id)?.originalName ?? id;
        return <ConfirmModal title="미디어 일괄 삭제"
          message={<>
            {bulkMediaUsage.loading ? <p>선택한 미디어 {bulkMediaDeleteTarget.length}개가 어디에 쓰이는지 확인하는 중…</p>
              : <p>선택한 미디어 {bulkMediaDeleteTarget.length}개 중 {free.length}개를 삭제할까요? 삭제한 내용은 복구할 수 없습니다.</p>}
            {inUse.length > 0 && <div className="mt-3 rounded border border-red/40 bg-red/10 px-3 py-2.5 text-[13px] text-red">
              <p className="font-medium">{inUse.length}개는 쓰고 있어 지우지 않고 건너뜁니다.</p>
              <ul className="mt-1 flex flex-col gap-0.5 pl-3">
                {inUse.map((id) => <li key={id} className="truncate">· {nameOf(id)} — {(usage?.get(id) ?? []).map((u) => u.name).join(', ')}</li>)}
              </ul>
              <p className="mt-2 text-text-dim">먼저 그곳에서 빼면 지울 수 있습니다.</p>
            </div>}
          </>}
          confirmLabel={free.length ? `${free.length}개 삭제` : '삭제'}
          confirmDisabled={bulkMediaUsage.loading || free.length === 0}
          confirmDisabledReason={bulkMediaUsage.loading ? '사용처를 확인하는 중입니다.' : '지울 수 있는 미디어가 없습니다.'}
          onCancel={() => setBulkMediaDeleteTarget(null)} onConfirm={async () => {
            setBulkBusy(true);
            let deleted = 0; const failed: string[] = []; let reason = '';
            try {
              for (const id of free) {
                try { await deleteMedia(id); onContentDeleted?.('media', id); deleted += 1; }
                catch (cause) { failed.push(id); if (!reason && cause instanceof Error) reason = cause.message; }
              }
              exitSelectMode();
              const notes = [inUse.length ? `${inUse.length}개는 사용 중이라 건너뜀` : '', failed.length ? `${failed.length}개 실패${reason ? `: ${reason}` : ''}` : '']
                .filter(Boolean).join(' · ');
              toast(`${deleted}개 삭제 완료${notes ? ` (${notes})` : ''}.`, failed.length ? 'error' : 'success');
            } finally { setBulkBusy(false); }
          }} />;
      })()}
      {bulkDeleteTarget && (
        <ConfirmModal title="슬라이드 일괄 삭제" message={`선택한 슬라이드 ${bulkDeleteTarget.length}개를 삭제할까요? 삭제한 내용은 복구할 수 없습니다.`}
          confirmLabel="삭제" onCancel={() => setBulkDeleteTarget(null)} onConfirm={async () => {
            if (bulkDeleteTarget.some((id) => !notices.some((n) => n.id === id && n.canWrite !== false && !isBrickBriefTemplateNotice(n)))) {
              throw new Error('삭제할 수 없는 슬라이드가 있습니다. 다시 선택해 주세요.');
            }
            setBulkBusy(true);
            try {
              const result = await onBulkDeleteNotices({ ids: bulkDeleteTarget });
              setSelectedNoticeIds(new Set());
              setSelectMode(false);
              toast(`${result.deleted.length}개 삭제 완료${result.skipped.length ? ` (${result.skipped.length}개 건너뜀)` : ''}.`, 'success');
            } finally { setBulkBusy(false); }
          }} />
      )}
      {/* 이미 있는 콘텐츠의 소유 그룹 이전은 서버가 조직 Owner에게만 허용한다(groupScope.classify, 2026-09-15 결정).
          미분류 콘텐츠를 처음 분류하는 것만 중앙 관리자도 된다. 그 밖의 사람에게 칸을 열면 저장 때
          "권한이 없어 요청을 처리할 수 없습니다"로 끝났다(SCDS-93). */}
      {noticeInfoTarget && notices.find((n) => n.id === noticeInfoTarget.id)?.canWrite !== false && (
        <NoticeEditModal notice={notices.find((n) => n.id === noticeInfoTarget.id) ?? noticeInfoTarget}
          canChooseGroup={canTransferGroup(notices.find((n) => n.id === noticeInfoTarget.id) ?? noticeInfoTarget)} onClose={() => setNoticeInfoTarget(null)} />
      )}
      {editingMedia && (
        <MediaEditModal item={scopedMediaList.find((m) => m.id === editingMedia.id) ?? editingMedia}
          canChooseGroup={canTransferGroup(scopedMediaList.find((m) => m.id === editingMedia.id) ?? editingMedia)} onClose={() => setEditingMedia(null)} onSaved={loadMedia} />
      )}
      {previewPick && (
        <ModalShell title="콘텐츠 미리보기" onClose={() => setPreviewPick(null)} panelClassName="w-[800px] max-w-[92vw]">
          <p className="text-[13px] text-text-dim">{previewPick.kind === 'media' ? previewPick.item.originalName : previewPick.item.name}</p>
          {previewPick.kind === 'media' && previewPick.item.mediaType === 'video' ? (
            <video controls src={api.mediaFileUrl(previewPick.item.id)} className="max-h-[65vh] w-full bg-black" />
          ) : previewPick.kind === 'media' && previewPick.item.mediaType === 'image' ? (
            <img src={api.mediaFileUrl(previewPick.item.id)} alt={previewPick.item.originalName} className="max-h-[65vh] w-full object-contain" />
          ) : previewPick.item.thumbnailRelPath ? (
            <img src={previewPick.kind === 'notice' ? api.noticeThumbnailUrl(previewPick.item.id, previewPick.item.thumbnailRelPath) : api.mediaThumbnailUrl(previewPick.item.id)}
              alt="저장된 콘텐츠 미리보기" className="max-h-[65vh] w-full object-contain" />
          ) : <p className="py-6 text-[13px] text-text-dim">저장된 미리보기 이미지가 없습니다.</p>}
        </ModalShell>
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  count,
  label,
}: {
  active: boolean;
  onClick: () => void;
  count: number;
  label: string;
}) {
  return (
    // 검수 C4 — 시각은 그대로, role=tab/aria-selected만 채운다.
    // 검수 C13 — 드로어가 좁아져도 라벨이 낱자로 꺾이지 않게 whitespace-nowrap + shrink-0.
    //            (지금은 min 280 클램프 덕에 안 보일 뿐, CSS 자체에 방어가 없었다.)
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'flex flex-none items-baseline gap-1.5 whitespace-nowrap px-1 py-1.5 border-b-2 transition-colors',
        active
          ? 'border-accent text-accent'
          : 'border-transparent text-text-dim hover:text-text',
      )}
    >
      <span className="text-[13px] font-semibold">{label}</span>
      <span className="font-mono text-[12px] uppercase tracking-monoWide text-text-dim">
        {count}
      </span>
    </button>
  );
}

// 타일 우상단 ⋮ 팝업 — 액션 행(children: 수정/삭제).
// hover 시 노출, 메뉴 열림 상태에서도 유지. onPick 버튼 위에 겹쳐 배치되므로
// 클릭 전파를 막아 타일 선택/재생이 함께 트리거되지 않게 한다.
// '폴더로 이동…'은 2026-09-16 대교 결정으로 없앴다 — 폴더가 없으니 옮길 곳도 없다.
function TileMenu({
  children,
  trailing,
}: {
  /** 앞쪽에 오는 항목(미리보기·수정·복제). */
  children?: React.ReactNode;
  /** 뒤쪽에 오는 항목(템플릿 지정·삭제). */
  trailing?: React.ReactNode;
}) {
  return (
    <span
      data-no-drag="true"
      onClick={(e) => e.stopPropagation()}
      className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 [&:has(details[open])]:opacity-100 transition-opacity duration-fast"
    >
      {/* 순서: 미리보기 → 수정 → (복제) → (템플릿) → 삭제(별색). 세 곳(카드·행·플레이리스트) 동일(2026-09-15 대교). */}
      <ToolbarMenu glyph="⋮" title="더보기" align="right">
        {children}
        {trailing}
      </ToolbarMenu>
    </span>
  );
}

function NoticeTile({
  notice,
  onPick,
  onPreview,
  onEditInfo,
  onEdit,
  onDuplicate,
  onSaveTemplate,
  onPublishIdle,
  onDelete,
  selectMode = false,
  selected = false,
  disabled = false,
  disabledReason,
  pickLabel,
}: {
  notice: Notice;
  onPick: () => void;
  onPreview: () => void;
  /** 슬라이드명·재생시간·폴더·공개 범위 모달. */
  onEditInfo: () => void;
  /** 캔버스 편집기. */
  onEdit: () => void;
  /** 이 슬라이드를 템플릿으로 복제해 새 슬라이드 생성. */
  onDuplicate?: () => void;
  /** Owner 전용 — 이 슬라이드의 복사본을 템플릿으로 저장. */
  onSaveTemplate?: (() => void) | undefined;
  /** 중앙 관리자 — 이 슬라이드를 조직 기본 대기 화면으로 게시(모달). */
  onPublishIdle?: (() => void) | undefined;
  onDelete: () => void;
  selectMode?: boolean;
  selected?: boolean;
  disabled?: boolean;
  /** 비활성 이유 — 숨기지 않고 왜 못 누르는지 붙인다. */
  disabledReason?: string;
  /** 썸네일 버튼의 접근 이름 — 클릭이 무엇을 하는지(추가/미리보기/선택)까지 담아 부모가 만든다(검수 C8). */
  pickLabel?: string;
}) {
  const isBrickBriefTemplate = isBrickBriefTemplateNotice(notice);
  return (
    <div
      className={cn(
        'group relative bg-panel-2 border rounded overflow-hidden transition-colors duration-fast',
        selected
          ? 'border-accent ring-2 ring-accent/50'
          : disabled
            ? 'border-line opacity-40'
            : 'border-line hover:border-accent',
      )}
    >
      <button
        type="button"
        onClick={onPick}
        disabled={disabled}
        {...(disabled && disabledReason ? { title: disabledReason } : {})}
        aria-label={pickLabel ?? notice.name}
        className={cn(
          'block w-full aspect-video bg-black relative',
          disabled && 'cursor-not-allowed',
        )}
      >
        {notice.thumbnailRelPath ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={api.noticeThumbnailUrl(notice.id, notice.thumbnailRelPath)}
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-mono text-[12px] text-text-dim">
            SCENE
          </div>
        )}
        <span className="absolute top-1.5 left-1.5 font-mono text-[12px] uppercase tracking-monoWide bg-purple/80 text-white px-1.5 py-[1px] rounded-sm">
          {isBrickBriefTemplate ? 'template' : 'scene'}
        </span>
        {isBrickBriefTemplate && (
          <span className="absolute top-1.5 right-1.5 font-mono text-[12px] uppercase tracking-monoWide bg-accent/90 text-bg px-1.5 py-[1px] rounded-sm">
            BRIEF
          </span>
        )}
        {selectMode && (
          <span
            className={cn(
              'absolute top-1.5 right-1.5 w-5 h-5 rounded-sm border-2 flex items-center justify-center font-mono text-[12px] font-bold',
              selected
                ? 'bg-accent border-accent text-bg'
                  : disabled
                    ? 'bg-panel/80 border-line text-text-dimmer'
                  : 'bg-panel/80 border-white/60 text-transparent',
            )}
          >
            {selected ? '✓' : disabled ? '🔒' : ''}
          </span>
        )}
      </button>
      {!selectMode && (
        <TileMenu
          trailing={<>
            {onSaveTemplate && <MenuRow label="템플릿으로 저장" onClick={onSaveTemplate} />}
            {onPublishIdle && <MenuRow label="대기 화면으로 게시…" onClick={onPublishIdle} />}
            {notice.canWrite !== false && !isBrickBriefTemplate && <>
              <MenuDivider />
              <MenuRow label="삭제" danger onClick={onDelete} />
            </>}
          </>}>
          <MenuRow label="미리보기" onClick={onPreview} />
          {/* '수정'은 미디어와 같은 정보 모달(이름·재생시간·폴더·공개 범위), 레이어 작업은 '디자인 편집'(2026-09-15 대교). */}
          {/* 송출 중 잠금은 숨기지 않고 비활성 + 사유 — 왜 못 여는지 보여야 한다(SCDS-102). 보통의 읽기 전용은 종전대로 숨긴다. */}
          {(notice.canWrite !== false || notice.writeBlockedReason) && <MenuRow label="수정" onClick={onEditInfo} disabled={notice.canWrite === false} {...(notice.writeBlockedReason ? { title: notice.writeBlockedReason } : {})} />}
          {(notice.canWrite !== false || notice.writeBlockedReason) && <MenuRow label="디자인 편집" onClick={onEdit} disabled={notice.canWrite === false} {...(notice.writeBlockedReason ? { title: notice.writeBlockedReason } : {})} />}
          {/* '템플릿으로 새 슬라이드 만들기'는 너무 길어 '복제'로(2026-09-15 대교). 원본은 그대로, 복제본이 편집기에서 열린다. */}
          {onDuplicate && <MenuRow label="복제" onClick={onDuplicate} />}
        </TileMenu>
      )}
      <div className="px-2.5 py-1.5">
        <div className="flex items-center gap-1.5 text-[13px] text-text">
          <span className="min-w-0 truncate">{notice.name}</span>
          <LiveMarks meta={notice} what="슬라이드" />
        </div>
      </div>
    </div>
  );
}

// 슬라이드 목록 보기(2026-09-16 대교: 라이브러리와 같은 격자/목록 옵션). 메뉴는 NoticeTile과 동일.
function NoticeListRow({
  notice,
  onPick,
  onPreview,
  onEditInfo,
  onEdit,
  onDuplicate,
  onSaveTemplate,
  onPublishIdle,
  onDelete,
  selectMode = false,
  selected = false,
  disabled = false,
  disabledReason,
  pickLabel,
}: {
  notice: Notice;
  onPick: () => void;
  onPreview: () => void;
  onEditInfo: () => void;
  onEdit: () => void;
  onDuplicate?: () => void;
  onSaveTemplate?: (() => void) | undefined;
  onPublishIdle?: (() => void) | undefined;
  onDelete: () => void;
  selectMode?: boolean;
  selected?: boolean;
  disabled?: boolean;
  /** 비활성 이유 — 숨기지 않고 왜 못 누르는지 붙인다. */
  disabledReason?: string;
  /** 썸네일 버튼의 접근 이름 — 클릭이 무엇을 하는지(추가/미리보기/선택)까지 담아 부모가 만든다(검수 C8). */
  pickLabel?: string;
}) {
  const isBrickBriefTemplate = isBrickBriefTemplateNotice(notice);
  const portrait = notice.canvasHeight > notice.canvasWidth;
  const noticeMeta = `${portrait ? '세로' : '가로'} · ${notice.canvasWidth}×${notice.canvasHeight} · ${notice.durationSec}초`;
  return (
    <div className={cn('group flex items-center gap-2 rounded border bg-panel-2 p-1.5 transition-colors duration-fast',
      selected ? 'border-accent ring-2 ring-accent/50' : disabled ? 'border-line opacity-40' : 'border-line hover:border-accent')}>
      {/* 체크박스는 썸네일 위 오버레이 — 선택 모드에 들어가도 썸네일·이름의 x가 밀리지 않는다(검수 S8). */}
      <button type="button" onClick={onPick} disabled={disabled} aria-label={pickLabel ?? notice.name}
        className={cn('h-12 w-20 flex-none overflow-hidden rounded-sm bg-black relative', disabled && 'cursor-not-allowed')}>
        {notice.thumbnailRelPath ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={api.noticeThumbnailUrl(notice.id, notice.thumbnailRelPath)} alt="" className="w-full h-full object-cover" draggable={false} />
        ) : (
          <span className="flex w-full h-full items-center justify-center font-mono text-[12px] text-text-dim">SCENE</span>
        )}
        {selectMode && (
          <span aria-hidden="true" className={cn('absolute top-1 left-1 h-5 w-5 rounded-sm border-2 flex items-center justify-center font-mono text-[12px] font-bold',
            selected ? 'bg-accent border-accent text-bg' : disabled ? 'bg-panel/80 border-line text-text-dim' : 'bg-panel/80 border-white/60 text-transparent')}>{selected ? '✓' : disabled ? '🔒' : ''}</span>
        )}
      </button>
      <button type="button" onClick={onPick} disabled={disabled} className="min-w-0 flex-1 text-left disabled:cursor-not-allowed">
        <div className="flex items-center gap-1.5 truncate text-[13px] text-text">
          <span className="truncate">{notice.name}</span>
          {isBrickBriefTemplate && <span className={cn(TAG_CLS, 'shrink-0')}>템플릿</span>}
          <LiveMarks meta={notice} what="슬라이드" />
        </div>
        {/* 메타는 한 덩어리 한 줄 — span 3개로 두면 좁은 드로어에서 "세/로", "10/초"로 낱자가 쪼개졌다(검수 S13).
            줄바꿈 대신 말줄임으로 흘려 카드 높이가 내용 따라 흔들리지 않게 한다. */}
        <div className="mt-0.5 truncate whitespace-nowrap font-mono text-[12px] uppercase tracking-monoWide text-text-dim" title={noticeMeta}>
          {noticeMeta}
        </div>
      </button>
      {!selectMode && <span
        onClick={(e) => e.stopPropagation()}
        className="relative flex-none opacity-0 group-hover:opacity-100 [&:has(details[open])]:opacity-100 transition-opacity duration-fast"
      >
        <ToolbarMenu glyph="⋮" title="더보기" align="right">
          <MenuRow label="미리보기" onClick={onPreview} />
          {(notice.canWrite !== false || notice.writeBlockedReason) && <MenuRow label="수정" onClick={onEditInfo} disabled={notice.canWrite === false} {...(notice.writeBlockedReason ? { title: notice.writeBlockedReason } : {})} />}
          {(notice.canWrite !== false || notice.writeBlockedReason) && <MenuRow label="디자인 편집" onClick={onEdit} disabled={notice.canWrite === false} {...(notice.writeBlockedReason ? { title: notice.writeBlockedReason } : {})} />}
          {onDuplicate && <MenuRow label="복제" onClick={onDuplicate} />}
          {onSaveTemplate && <MenuRow label="템플릿으로 저장" onClick={onSaveTemplate} />}
          {onPublishIdle && <MenuRow label="대기 화면으로 게시…" onClick={onPublishIdle} />}
          {notice.canWrite !== false && !isBrickBriefTemplate && <><MenuDivider /><MenuRow label="삭제" danger onClick={onDelete} /></>}
        </ToolbarMenu>
      </span>}
    </div>
  );
}

function MediaTile({
  item,
  onPick,
  onPreview,
  onEdit,
  onDelete,
  selectMode = false,
  selected = false,
  disabled = false,
  disabledReason,
  pickLabel,
}: {
  item: MediaItem;
  onPick: () => void;
  onPreview: () => void;
  onEdit: () => void;
  onDelete: () => void;
  selectMode?: boolean;
  selected?: boolean;
  disabled?: boolean;
  /** 비활성 이유 — 숨기지 않고 왜 못 누르는지 붙인다. */
  disabledReason?: string;
  /** 썸네일 버튼의 접근 이름 — 클릭이 무엇을 하는지(추가/미리보기/선택)까지 담아 부모가 만든다(검수 C8). */
  pickLabel?: string;
}) {
  const hasThumb = item.thumbnailRelPath !== null;
  const isImage = item.mediaType === 'image';
  const thumbUrl = hasThumb
    ? api.mediaThumbnailUrl(item.id)
    : isImage
      ? api.mediaFileUrl(item.id)
      : null;

  return (
    <div className={cn('group relative bg-panel-2 border rounded overflow-hidden transition-colors duration-fast',
      selected ? 'border-accent ring-2 ring-accent/50' : disabled ? 'border-line opacity-40' : 'border-line hover:border-accent')}>
      <button
        type="button"
        onClick={onPick}
        disabled={disabled}
        {...(disabled && disabledReason ? { title: disabledReason } : {})}
        aria-label={pickLabel ?? item.originalName}
        className={cn('block w-full aspect-video bg-black relative', disabled && 'cursor-not-allowed')}
      >
        {selectMode && (
          <span className={cn('absolute top-1.5 right-1.5 z-10 w-5 h-5 rounded-sm border-2 flex items-center justify-center font-mono text-[12px] font-bold',
            selected ? 'bg-accent border-accent text-bg' : disabled ? 'bg-panel/80 border-line text-text-dimmer' : 'bg-panel/80 border-white/60 text-transparent')}>
            {selected ? '✓' : disabled ? '🔒' : ''}
          </span>
        )}
        {thumbUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={thumbUrl}
            alt={item.originalName}
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-mono text-[12px] text-text-dim">
            {mediaTypeLabel(item).toUpperCase()}
          </div>
        )}
        <span className="absolute top-1.5 left-1.5 font-mono text-[12px] uppercase tracking-monoWide bg-black/70 text-white px-1.5 py-[1px] rounded-sm">
          {mediaTypeLabel(item)}
        </span>
        {item.durationSec !== null && (
          <span className="absolute bottom-1.5 right-1.5 font-mono text-[12px] bg-black/70 text-white px-1.5 py-[1px] rounded-sm">
            {formatDuration(item.durationSec)}
          </span>
        )}
        {/* NBL_REQ_408 — 트랜스코딩 처리 상태 뱃지 */}
        {item.processingStatus === 'pending' && (
          <span className="absolute bottom-1.5 left-1.5 font-mono text-[12px] bg-black/70 text-white px-1.5 py-[1px] rounded-sm">
            변환 대기
          </span>
        )}
        {item.processingStatus === 'processing' && (
          <span className="absolute bottom-1.5 left-1.5 font-mono text-[12px] bg-accent/90 text-bg px-1.5 py-[1px] rounded-sm">
            {item.transcodeProgress ? `변환 중 ${item.transcodeProgress.percent}%` : '변환 중…'}
          </span>
        )}
        {item.processingStatus === 'failed' && (
          <span className="absolute bottom-1.5 left-1.5 font-mono text-[12px] bg-red/90 text-white px-1.5 py-[1px] rounded-sm">
            변환 실패
          </span>
        )}
      </button>
      {!selectMode && <TileMenu
        trailing={item.canWrite !== false && <><MenuDivider /><MenuRow label="삭제" danger onClick={onDelete} /></>}>
        <MenuRow label="미리보기" onClick={onPreview} />
        {(item.canWrite !== false || item.writeBlockedReason) && <MenuRow label="수정" onClick={onEdit} disabled={item.canWrite === false} {...(item.writeBlockedReason ? { title: item.writeBlockedReason } : {})} />}
      </TileMenu>}
      <div className="px-2.5 py-1.5">
        <div className="flex items-center gap-1.5 text-[13px] text-text">
          <span className="min-w-0 truncate">{item.originalName}</span>
          <LiveMarks meta={item} what="미디어" />
        </div>
      </div>
    </div>
  );
}

/** 서버 변환 상태 한 줄 — 대기·변환 중·실패, 끝났으면 1080×1920 기기가 받는 변환본 크기(2026-10-07). */
export function mediaProcessingNote(item: Pick<MediaItem, 'processingStatus' | 'variants' | 'transcodeProgress'>): { text: string; tone: 'dim' | 'accent' | 'red' } | null {
  if (item.processingStatus === 'pending') return { text: '변환 대기', tone: 'dim' };
  if (item.processingStatus === 'processing') {
    const p = item.transcodeProgress;
    if (!p) return { text: '변환 중…', tone: 'accent' };
    const eta = p.etaSec === null ? '남은 시간 계산 중' : p.etaSec < 60 ? '1분 이내' : `약 ${Math.round(p.etaSec / 60)}분 남음`;
    return { text: `변환 중 ${p.percent}% · ${p.step}/${p.steps} · ${eta}`, tone: 'accent' };
  }
  if (item.processingStatus === 'failed') return { text: '변환 실패 · 원본 전송', tone: 'red' };
  const sized = (item.variants ?? []).filter(v => typeof v.sizeBytes === 'number');
  const device = sized.filter(v => v.height <= 1920).sort((a, b) => b.height - a.height)[0] ?? sized.sort((a, b) => a.height - b.height)[0];
  return device ? { text: `기기용 ${Math.max(1, Math.round(device.sizeBytes! / 1024 / 1024))}MB`, tone: 'dim' } : null;
}
const NOTE_TONE = { dim: 'text-text-dim', accent: 'text-accent', red: 'text-red' } as const;

const sizeText = (bytes: number) => bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))}KB` : `${Math.round(bytes / 1024 / 1024)}MB`;
/** 서버에서 실제로 쓰는 용량(원본 + 변환본). 먼저 올라온 항목과 같은 파일이면 공유라 더 쓰지 않는다(2026-10-12). */
export function mediaStorageText(item: Pick<MediaItem, 'sizeBytes' | 'storageBytes' | 'storageAddedBytes'>): string {
  const total = item.storageBytes ?? item.sizeBytes;
  if (item.storageAddedBytes === 0 && total > 0) return '공유 · 0MB';
  return sizeText(item.storageAddedBytes ?? total);
}

function MediaListRow({
  item,
  onPick,
  onPreview,
  onEdit,
  onDelete,
  selectMode = false,
  selected = false,
  disabled = false,
  disabledReason,
  pickLabel,
}: {
  item: MediaItem;
  onPick: () => void;
  onPreview: () => void;
  onEdit: () => void;
  onDelete: () => void;
  selectMode?: boolean;
  selected?: boolean;
  disabled?: boolean;
  /** 비활성 이유 — 숨기지 않고 왜 못 누르는지 붙인다. */
  disabledReason?: string;
  /** 썸네일 버튼의 접근 이름 — 클릭이 무엇을 하는지(추가/미리보기/선택)까지 담아 부모가 만든다(검수 C8). */
  pickLabel?: string;
}) {
  const hasThumb = item.thumbnailRelPath !== null;
  const isImage = item.mediaType === 'image';
  const thumbUrl = hasThumb
    ? api.mediaThumbnailUrl(item.id)
    : isImage
      ? api.mediaFileUrl(item.id)
      : null;
  const mediaMeta = [
    mediaTypeLabel(item),
    mediaStorageText(item),
    ...(item.durationSec !== null ? [formatDuration(item.durationSec)] : []),
  ].join(' · ');
  const processing = mediaProcessingNote(item);

  return (
    <div className={cn('group flex items-center gap-2 rounded border bg-panel-2 p-1.5 transition-colors duration-fast',
      selected ? 'border-accent ring-2 ring-accent/50' : disabled ? 'border-line opacity-40' : 'border-line hover:border-accent')}>
      {/* 체크박스는 썸네일 위 오버레이 — 선택 모드 진입으로 행의 x가 밀리지 않게(검수 S8). */}
      <button
        type="button"
        onClick={onPick}
        disabled={disabled}
        {...(disabled && disabledReason ? { title: disabledReason } : {})}
        aria-label={pickLabel ?? item.originalName}
        className={cn('h-12 w-20 flex-none overflow-hidden rounded-sm bg-black relative', disabled && 'cursor-not-allowed')}
      >
        {thumbUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={thumbUrl}
            alt=""
            className="w-full h-full object-cover"
            draggable={false}
          />
        ) : (
          <span className="flex w-full h-full items-center justify-center font-mono text-[12px] text-text-dim">
            {mediaTypeLabel(item).toUpperCase()}
          </span>
        )}
        {selectMode && (
          <span aria-hidden="true" className={cn('absolute top-1 left-1 h-5 w-5 rounded-sm border-2 flex items-center justify-center font-mono text-[12px] font-bold',
            selected ? 'bg-accent border-accent text-bg' : disabled ? 'bg-panel/80 border-line text-text-dim' : 'bg-panel/80 border-white/60 text-transparent')}>{selected ? '✓' : disabled ? '🔒' : ''}</span>
        )}
      </button>
      <button type="button" onClick={onPick} disabled={disabled} className="min-w-0 flex-1 text-left disabled:cursor-not-allowed">
        <div className="flex items-center gap-1.5 truncate text-[13px] text-text">
          <span className="truncate">{item.originalName}</span>
          <LiveMarks meta={item} what="미디어" />
        </div>
        {/* 메타는 한 줄 한 덩어리 + 말줄임 — 낱말 단위로 꺾여 행 높이가 흔들리던 문제(검수 S13). */}
        <div className="mt-0.5 truncate whitespace-nowrap font-mono text-[12px] uppercase tracking-monoWide text-text-dim" title={processing ? `${mediaMeta} · ${processing.text}` : mediaMeta}>
          {mediaMeta}
          {processing && <span className={cn('normal-case', NOTE_TONE[processing.tone])}> · {processing.text}</span>}
        </div>
      </button>
      {!selectMode && <span
        onClick={(e) => e.stopPropagation()}
        className="relative flex-none opacity-0 group-hover:opacity-100 [&:has(details[open])]:opacity-100 transition-opacity duration-fast"
      >
        <ToolbarMenu glyph="⋮" title="더보기" align="right">
          <MenuRow label="미리보기" onClick={onPreview} />
          {(item.canWrite !== false || item.writeBlockedReason) && <MenuRow label="수정" onClick={onEdit} disabled={item.canWrite === false} {...(item.writeBlockedReason ? { title: item.writeBlockedReason } : {})} />}
          {item.canWrite !== false && <><MenuDivider /><MenuRow label="삭제" danger onClick={onDelete} /></>}
        </ToolbarMenu>
      </span>}
    </div>
  );
}

// 송출 중 편성·승인자 잠금 표시(SCDS-102). 처음엔 '편성 중'·'승인자만 수정' 글자 태그였는데 이름을 밀어내
// 좁은 카드에서 파일 이름이 통째로 사라졌다(2026-10-01 대교) — 아이콘만 두고 뜻은 title·aria-label로 전한다.
// 보통의 읽기 전용(다른 그룹 자료 등)은 종전대로 표시하지 않는다 — 전부에 자물쇠가 붙으면 신호가 아니라 소음이다.
function LiveMarks({ meta, what }: { meta: ContentAccessMetadata; what: string }): JSX.Element | null {
  const { scheduled, writeBlockedReason: lockReason } = meta;
  if (!scheduled && !lockReason) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-1 text-text-dim">
      {scheduled && (
        <span role="img" aria-label="편성 중" aria-description={liveTip(what, meta.scheduledIn)} className="inline-flex"><OnAirIcon /></span>
      )}
      {lockReason && (
        <span role="img" aria-label="승인자만 수정" aria-description={lockReason} className="inline-flex"><LockIcon /></span>
      )}
    </span>
  );
}

// '편성 중' 말풍선 — 어느 플레이리스트에서(플레이리스트 자신이면 어느 스케줄이) 쓰는지 이름으로 보여 준다(SCDS-110).
// 이름 없이 '재생하고 있습니다'만 있으면, 플레이리스트를 다 열어 봐도 못 찾는 콘텐츠가 왜 편성 중인지 알 수 없었다.
function liveTip(what: string, where: string[] | undefined): string {
  if (!where?.length) return `편성 중 — 승인된 스케줄이 이 ${what}를 재생하고 있습니다.`;
  // 머리는 한 줄로 짧게 — 길면 단어 중간에서 꺾이고 정작 봐야 할 이름이 아래로 밀린다.
  const head = what === '플레이리스트' ? '편성 중 — 이 플레이리스트가 걸린 스케줄' : '편성 중 — 스케줄에 걸린 플레이리스트';
  return [head, ...where.slice(0, 5).map(line => `· ${line}`), ...(where.length > 5 ? [`외 ${where.length - 5}곳`] : [])].join('\n');
}

// 송출 중 — 가운데 점에서 전파가 퍼지는 모양.
function OnAirIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="8" r="1.7" fill="currentColor" />
      <path d="M5.3 10.7a3.8 3.8 0 0 1 0-5.4M10.7 5.3a3.8 3.8 0 0 1 0 5.4M3 13a7 7 0 0 1 0-10M13 3a7 7 0 0 1 0 10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

// 승인자만 수정 — 자물쇠.
function LockIcon(): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="3.5" y="7" width="9" height="6.5" rx="1.3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.5 7V5.2a2.5 2.5 0 0 1 5 0V7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function ColumnsIcon(): JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <rect x="2" y="3" width="4" height="9" rx="0.8" stroke="currentColor" strokeWidth="1.4" />
      <rect x="9" y="3" width="4" height="9" rx="0.8" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function ListIcon(): JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
      <path d="M3 4H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M3 7.5H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M3 11H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

// 드래그 순서변경 핸들 — 가로 2줄 그립(문서 스타일).
function GripIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 6H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M3 10H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec - m * 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
